import { CloudUploadOutlined, DeleteOutlined, DownloadOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Drawer, Form, Image, Input, InputNumber, Modal, Pagination, Progress, Radio, Row, Select, Space, Steps, Table, Tabs, Tag, Tooltip, Upload, message, notification } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPublishTask, createXhsNote, deleteContent, downloadContent, executePublishNow, getContentDetail, getContentList, listAgents, modifyContentDetail, previewAgentDispatch, previewContentMetadata, previewXhsNoteSummary, previewXhsNoteTags, searchContent, searchPublishAccounts, searchPublishList, uploadContent } from '../api';
import { useAuth } from '../auth/AuthContext';
import { resolveErrorMessage } from '../utils/errorMessage';
import { fixMojibakeUtf8 } from '../utils/text';
import XhsAccountCheckButton from './XhsAccountCheckButton';
import DouyinAccountCheckButton from './DouyinAccountCheckButton';
import KuaishouAccountCheckButton from './KuaishouAccountCheckButton';

const { RangePicker } = DatePicker;
const LOCAL_AGENT_PLATFORM_CODES = new Set(['douyin', 'kuaishou', 'weixin_channels', 'wechat_channels']);
const READY_AUTH_STATES = new Set(['ready', 'valid', 'fresh']);

const normalizePlatformCode = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const map = {
    '抖音': 'douyin',
    '快手': 'kuaishou',
    '微信视频号': 'weixin_channels',
    '视频号': 'weixin_channels',
    'wechat_channels': 'weixin_channels',
  };
  const lowered = raw.toLowerCase();
  return map[raw] || map[lowered] || lowered;
};

const ContentList = ({ contents, onEdit, onDelete, onPublish, onDownload, onFilter }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [detailForm] = Form.useForm();
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedTypeById, setSelectedTypeById] = useState({});
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [contentDetail, setContentDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('preview');
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [xhsNoteModalVisible, setXhsNoteModalVisible] = useState(false);
  const [xhsNoteForm] = Form.useForm();
  const [xhsNoteImageFileList, setXhsNoteImageFileList] = useState([]);
  const [xhsNoteCreating, setXhsNoteCreating] = useState(false);
  const [xhsNoteSummaryGenerating, setXhsNoteSummaryGenerating] = useState(false);
  const [xhsNoteTagsGenerating, setXhsNoteTagsGenerating] = useState(false);
  const [xhsNoteDragUid, setXhsNoteDragUid] = useState(null);
  const [xhsNoteDropHint, setXhsNoteDropHint] = useState(null); // { uid, position: 'before'|'after'|'end' }
  const xhsNotePreviewUrlByUidRef = useRef(new Map());
  const [uploadForm] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [uploadItems, setUploadItems] = useState([]);
  const [selectedUploadUid, setSelectedUploadUid] = useState(null);
  const [uploadItemForm] = Form.useForm();
  const [previewing, setPreviewing] = useState(false);
  const [previewingAction, setPreviewingAction] = useState(null); // 'title' | 'all' | null
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ total: 0, done: 0, ok: 0, failed: 0 });
  const [batchFailures, setBatchFailures] = useState([]);
  const [batchSkipDone, setBatchSkipDone] = useState(true);
  const [globalTextSource, setGlobalTextSource] = useState('asr'); // asr|image|auto
  const [globalOcrMaxFrames, setGlobalOcrMaxFrames] = useState(6);
  const suppressUploadItemSyncRef = useRef(false);
  const [uploading, setUploading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  // 发布账号抽屉相关状态
  const [publishAccountDrawerVisible, setPublishAccountDrawerVisible] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountDataSource, setAccountDataSource] = useState([]);
  const [accountPagination, setAccountPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [currentPublishRecord, setCurrentPublishRecord] = useState(null);
  const [selectedAccountKeys, setSelectedAccountKeys] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const selectedAccountMapRef = useRef(new Map());
  // 发布时间选择抽屉相关状态
  const [publishTimeDrawerVisible, setPublishTimeDrawerVisible] = useState(false);
  const [publishType, setPublishType] = useState('immediate'); // 'immediate' 或 'scheduled'
  const [dispatchMode, setDispatchMode] = useState('default'); // default | preferred
  const [preferredAgentId, setPreferredAgentId] = useState('');
  const [dispatchAgents, setDispatchAgents] = useState([]);
  const [dispatchAgentsLoading, setDispatchAgentsLoading] = useState(false);
  const [publishTimeForm] = Form.useForm();
  const [publishSubmitting, setPublishSubmitting] = useState(false);
  const [optimisticPublishingByContentId, setOptimisticPublishingByContentId] = useState({});
  const publishPollTokenRef = useRef(0);
  const listQueryModeRef = useRef('list'); // 'list' | 'search'
  const publishSubmitAbortRef = useRef(null);
  // 发布账号筛选表单
  const [accountFilterForm] = Form.useForm();
  const isAbortError = (error) => (
    error?.name === 'AbortError'
    || error?.code === 20
    || /abort/i.test(String(error?.message || ''))
  );

  const abortPublishSubmitRequest = () => {
    const controller = publishSubmitAbortRef.current;
    if (!controller) return;
    publishSubmitAbortRef.current = null;
    try {
      controller.abort();
    } catch (_) {
      // ignore
    }
  };

  const beginPublishSubmitRequest = () => {
    abortPublishSubmitRequest();
    const controller = new AbortController();
    publishSubmitAbortRef.current = controller;
    return controller;
  };

  useEffect(() => () => {
    const controller = publishSubmitAbortRef.current;
    publishSubmitAbortRef.current = null;
    if (!controller) return;
    try {
      controller.abort();
    } catch (_) {
      // ignore
    }
  }, []);

  const copyToClipboard = async (text, { successMessage = '已复制' } = {}) => {
    const value = String(text ?? '').trim();
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        message.success(successMessage);
        return;
      }
    } catch (_) {
      // fallback below
    }
    try {
      const el = document.createElement('textarea');
      el.value = value;
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      message.success(successMessage);
    } catch (_) {
      message.error('复制失败，请手动复制');
    }
  };

  const openPublishNotification = ({ key, title, description, publishId, contentId, durationSeconds = 10 } = {}) => {
    const actions = (
      <Space size="small">
        <Button
          type="link"
          size="small"
          style={{ padding: '0 4px' }}
          onClick={() => navigate('/content?tab=published')}
        >
          去已发布内容
        </Button>
        {publishId ? (
          <Button
            type="link"
            size="small"
            style={{ padding: '0 4px' }}
            onClick={() => copyToClipboard(publishId, { successMessage: '已复制发布ID' })}
          >
            复制发布ID
          </Button>
        ) : null}
        {contentId ? (
          <Button
            type="link"
            size="small"
            style={{ padding: '0 4px' }}
            onClick={() => copyToClipboard(contentId, { successMessage: '已复制内容ID' })}
          >
            复制内容ID
          </Button>
        ) : null}
      </Space>
    );

    notification.open({
      key,
      message: title || '发布状态',
      description,
      btn: actions,
      duration: durationSeconds,
      placement: 'topRight',
    });
  };

  const startPollPublishRecord = ({ contentId, notificationKey } = {}) => {
    if (!contentId || !notificationKey) return;
    const token = publishPollTokenRef.current + 1;
    publishPollTokenRef.current = token;

    const maxAttempts = 5;
    const intervalMs = 2000;
    const statusNames = {
      success: '成功',
      failed: '失败',
      pending: '待发布',
      publishing: '发布中',
    };

    const attempt = async (idx) => {
      if (publishPollTokenRef.current !== token) return;
      try {
        const resp = await searchPublishList({
          page: 1,
          page_size: 20,
          filters: { content_id: contentId },
          sort: { field: 'publish_time', order: 'desc' },
        });
        const items = Array.isArray(resp?.items) ? resp.items : [];
        const matched = items.filter((it) => String(it?.content_id || '') === String(contentId));
        if (resp?.status === 'success' && matched.length > 0) {
          const latest = matched[0];
          const status = statusNames[latest?.status] || latest?.status || '-';
          openPublishNotification({
            key: notificationKey,
            title: '已生成发布记录',
            publishId: latest?.publish_id,
            contentId,
            description: (
              <div>
                <div>发布ID：{latest?.publish_id || '-'}</div>
                <div>状态：{status}</div>
              </div>
            ),
            durationSeconds: 10,
          });
          return;
        }
      } catch (_) {
        // ignore: 轮询时不打断用户
      }

      if (idx >= maxAttempts) {
        openPublishNotification({
          key: notificationKey,
          title: '已提交立即发布',
          contentId,
          description: '发布处理可能需要更久，请稍后到“已发布内容”查看状态。',
          durationSeconds: 10,
        });
        return;
      }

      setTimeout(() => attempt(idx + 1), intervalMs);
    };

    setTimeout(() => attempt(1), intervalMs);
  };

  // 加载数据
  const loadData = async (params = {}) => {
    listQueryModeRef.current = 'list';
    setLoading(true);
    try {
      // 优先使用传入的参数，如果没有则使用当前分页状态
      const page = params.page !== undefined ? params.page : pagination.current;
      const page_size = params.page_size !== undefined ? params.page_size : pagination.pageSize;

      const response = await getContentList({
        page,
        page_size,
        ...params,
      });

      if (response.status === 'success') {
        const now = Date.now();
        const optimisticTtlMs = 2 * 60 * 1000;
        const idsToClear = [];

        // 映射字段：将API返回的字段映射到表格需要的字段（保持 risk_flag 等布尔字段不变）
        const mappedData = response.items.map((item) => ({
          ...item,
          category_level1: item.primary_category,
          category_level2: item.secondary_category,
          risk_flag: item.risk_flag,
          manual_review_required: item.manual_review_required,
          is_manual_reviewed: item.is_manual_reviewed ?? false,
          status: (() => {
            const contentId = item?.content_id;
            const optimisticTs = optimisticPublishingByContentId?.[contentId];
            if (optimisticTs && now - optimisticTs < optimisticTtlMs) {
              // 后端状态仍然显示待发布时，用“发布中”给用户即时反馈
              if (item?.status === '待发布') return '发布中';
              // 后端状态已变化，清理乐观态
              idsToClear.push(contentId);
            }
            return item?.status;
          })(),
        }));

        setDataSource(mappedData);
        // 更新分页状态，包括当前页码和每页条数
        setPagination({
          current: page,
          pageSize: page_size,
          total: response.total,
        });

        if (idsToClear.length > 0) {
          setOptimisticPublishingByContentId((prev) => {
            const next = { ...(prev || {}) };
            idsToClear.forEach((id) => { if (id) delete next[id]; });
            return next;
          });
        }
      }
    } catch (error) {
      console.error('加载数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyOptimisticPublishingStatus = (items = []) => {
    const now = Date.now();
    const optimisticTtlMs = 2 * 60 * 1000;
    const idsToClear = [];

    const nextItems = (items || []).map((item) => {
      const contentId = item?.content_id;
      const optimisticTs = optimisticPublishingByContentId?.[contentId];
      if (!optimisticTs) return item;
      if (now - optimisticTs < optimisticTtlMs) {
        if (item?.status === '待发布') return { ...item, status: '发布中' };
        idsToClear.push(contentId);
        return item;
      }
      idsToClear.push(contentId);
      return item;
    });

    if (idsToClear.length > 0) {
      setOptimisticPublishingByContentId((prev) => {
        const next = { ...(prev || {}) };
        idsToClear.forEach((id) => { if (id) delete next[id]; });
        return next;
      });
    }

    return nextItems;
  };

  const buildSearchParamsFromForm = (page, pageSize) => {
    const values = form.getFieldsValue();
    const {
      tags,
      ingested_at_range,
      content_id,
      creator_user_id,
      author,
      content_type,
      status,
      source,
      available_platforms,
      risk_flag,
      manual_review_required,
      is_manual_reviewed,
      title,
      keyword,
    } = values;

    // 构建 filters 对象，过滤掉 undefined 值
    const filters = {};

    if (content_type !== undefined && content_type !== null) {
      filters.content_type = Array.isArray(content_type) ? content_type : [content_type];
    }
    if (creator_user_id) {
      filters.creator_user_id = creator_user_id;
    }
    if (author) {
      filters.author = author;
    }
    if (status !== undefined && status !== null) {
      filters.status = Array.isArray(status) ? status : [status];
    }
    if (source) {
      filters.source = source;
    }
    if (title) {
      filters.title = title.trim();
    }
    if (content_id) {
      filters.content_id = String(content_id).trim();
    }
    if (tags) {
      // 标签：输入框中用逗号分隔，转换为数组
      const tagsArray = tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      if (tagsArray.length > 0) {
        filters.tags = tagsArray;
      }
    }
    if (available_platforms !== undefined && available_platforms !== null) {
      filters.available_platforms = Array.isArray(available_platforms) ? available_platforms : [available_platforms];
    }
    if (risk_flag !== undefined && risk_flag !== null) {
      filters.risk_flag = risk_flag;
    }
    if (manual_review_required !== undefined && manual_review_required !== null) {
      filters.manual_review_required = manual_review_required;
    }
    if (is_manual_reviewed !== undefined && is_manual_reviewed !== null) {
      filters.is_manual_reviewed = is_manual_reviewed;
    }
    if (ingested_at_range) {
      // 入库时间：DatePicker.RangePicker，转换为后端需要的 from/to 结构
      const fromDate = ingested_at_range[0]?.toDate ? ingested_at_range[0].toDate() : ingested_at_range[0];
      const toDate = ingested_at_range[1]?.toDate ? ingested_at_range[1].toDate() : ingested_at_range[1];
      filters.ingested_at_range = {
        from: fromDate.toISOString(),
        to: toDate.toISOString(),
      };
    }

    const searchParams = {
      page,
      page_size: pageSize,
      keyword: keyword ? keyword.trim() : '',
      filters: Object.keys(filters).length > 0 ? filters : undefined,
      sort: {
        field: 'ingested_at',
        order: 'desc',
      },
    };

    if (!searchParams.filters) delete searchParams.filters;
    return searchParams;
  };

  const loadSearchData = async (page = pagination.current, pageSize = pagination.pageSize) => {
    listQueryModeRef.current = 'search';
    setLoading(true);
    try {
      const searchParams = buildSearchParamsFromForm(page, pageSize);
      const response = await searchContent(searchParams);

      if (response.status === 'success') {
        const mappedData = (response.items || []).map((item) => ({
          ...item,
          category_level1: item.primary_category,
          category_level2: item.secondary_category,
          risk_flag: item.risk_flag,
          manual_review_required: item.manual_review_required,
          is_manual_reviewed: item.is_manual_reviewed ?? false,
        }));

        const withOptimistic = applyOptimisticPublishingStatus(mappedData);
        setDataSource(withOptimistic);
        setPagination({
          ...pagination,
          total: response.total,
          current: response.page ?? page,
          pageSize: response.page_size ?? pageSize,
        });
      }
    } catch (error) {
      console.error('检索失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const reloadListAfterDelete = async (deletedIds = []) => {
    const ids = Array.from(new Set((deletedIds || []).filter(Boolean)));
    const currentPageIds = (dataSource || []).map((item) => item?.content_id).filter(Boolean);
    const deletedOnCurrentPage = ids.filter((id) => currentPageIds.includes(id)).length;
    const shouldBackPage = pagination.current > 1 && currentPageIds.length > 0 && deletedOnCurrentPage >= currentPageIds.length;
    const nextPage = shouldBackPage ? pagination.current - 1 : pagination.current;

    if (listQueryModeRef.current === 'search') {
      await loadSearchData(nextPage, pagination.pageSize);
      return;
    }
    await loadData({ page: nextPage, page_size: pagination.pageSize });
  };

  const reloadListAfterUpdate = async () => {
    if (listQueryModeRef.current === 'search') {
      await loadSearchData(pagination.current, pagination.pageSize);
      return;
    }
    await loadData({ page: pagination.current, page_size: pagination.pageSize });
  };

  // 初始加载数据
  useEffect(() => {
    loadData();
  }, []);

  const normalizeContentType = (raw) => {
    const t = String(raw || '').trim();
    if (!t) return '';
    const normalized = t.toLowerCase();
    if (normalized === 'video') return '视频';
    if (normalized === 'image_text' || normalized === 'imagetext' || normalized === 'article') return '图文';
    return t;
  };

  const selectedRowKeySet = useMemo(() => new Set(selectedRowKeys || []), [selectedRowKeys]);

  // 当翻页/检索导致 dataSource 更新时，补全当前页已选中项的类型信息（跨页选择需要）
  useEffect(() => {
    if (!dataSource || dataSource.length === 0) return;
    if (!selectedRowKeys || selectedRowKeys.length === 0) return;

    setSelectedTypeById((prev) => {
      let changed = false;
      const next = { ...(prev || {}) };
      for (const row of dataSource) {
        const id = row?.content_id;
        if (!id) continue;
        if (!selectedRowKeySet.has(id)) continue;
        const t = normalizeContentType(row?.content_type);
        if (t && next[id] !== t) {
          next[id] = t;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [dataSource, selectedRowKeySet, selectedRowKeys]);

  // 选中项清空时，清理类型缓存，避免残留影响按钮显示
  useEffect(() => {
    if ((selectedRowKeys || []).length === 0) {
      setSelectedTypeById({});
    }
  }, [selectedRowKeys]);

  const currentPageContentIds = (dataSource || [])
    .map((item) => item.content_id)
    .filter(Boolean);

  const isAllCurrentPageSelected = currentPageContentIds.length > 0
    && currentPageContentIds.every((id) => selectedRowKeys.includes(id));

  const toggleSelectAllCurrentPage = () => {
    if (currentPageContentIds.length === 0) return;

    setSelectedRowKeys((prevKeys) => {
      const otherPageSelected = (prevKeys || []).filter((key) => !currentPageContentIds.includes(key));
      const prevAllCurrent = currentPageContentIds.every((id) => (prevKeys || []).includes(id));
      const nextKeys = prevAllCurrent
        ? otherPageSelected
        : Array.from(new Set([...otherPageSelected, ...currentPageContentIds]));

      setSelectedTypeById((prev) => {
        const next = {};
        for (const k of nextKeys) {
          if (prev && prev[k]) next[k] = prev[k];
        }
        // 如果是“全选本页”，补全本页所有内容的类型
        if (!prevAllCurrent) {
          for (const row of dataSource || []) {
            const id = row?.content_id;
            if (!id) continue;
            if (!nextKeys.includes(id)) continue;
            const t = normalizeContentType(row?.content_type);
            if (t) next[id] = t;
          }
        }
        return next;
      });

      return nextKeys;
    });
  };

  // 格式化时间戳
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    try {
      const date = new Date(dateTimeString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      return dateTimeString;
    }
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      '已发布': { color: 'success', text: '已发布' },
      '待发布': { color: 'warning', text: '待发布' },
      '发布中': { color: 'processing', text: '发布中' },
      '草稿': { color: 'warning', text: '草稿' },
      '已下线': { color: 'error', text: '已下线' },
      '作废': { color: 'error', text: '作废' },
    };
    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除内容ID为 "${id}" 的内容吗？`,
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await deleteContent({
            content_ids: [id],
          });

          if (response.status === 'success') {
            // 检查删除结果
            if (response.deleted_ids && response.deleted_ids.length > 0) {
              message.success('删除成功');
              setSelectedRowKeys((prev) => (prev || []).filter((key) => key !== id));
              // 刷新数据
              await reloadListAfterDelete([id]);
              // 如果父组件有 onDelete 回调，也调用它
              if (onDelete) {
                onDelete(id);
              }
              // 返回成功，确保弹窗关闭
              return Promise.resolve();
            } else if (response.not_found_ids && response.not_found_ids.length > 0) {
              message.warning('内容不存在或已被删除');
              // 即使未找到，也刷新列表
              await reloadListAfterDelete([id]);
              return Promise.resolve();
            } else {
              message.warning('删除失败');
              return Promise.reject(new Error('删除失败'));
            }
          } else {
            message.error('删除失败');
            return Promise.reject(new Error('删除失败'));
          }
        } catch (error) {
          console.error('删除失败:', error);
          message.error('删除失败，请稍后重试');
          return Promise.reject(error);
        }
      },
    });
  };

  // 加载发布账号列表
  const loadPublishAccounts = async (params = {}) => {
    setAccountLoading(true);
    try {
      const page = params.page !== undefined ? params.page : accountPagination.current;
      const page_size = params.page_size !== undefined ? params.page_size : accountPagination.pageSize;

      // 获取表单筛选条件
      const filterValues = accountFilterForm.getFieldsValue();
      const filters = {};

      if (filterValues.platforms && filterValues.platforms.length > 0) {
        filters.platforms = Array.isArray(filterValues.platforms) ? filterValues.platforms : [filterValues.platforms];
      }
      if (filterValues.status && filterValues.status.length > 0) {
        filters.status = Array.isArray(filterValues.status) ? filterValues.status : [filterValues.status];
      }

      const requestParams = {
        page,
        page_size,
        keyword: params.keyword || '',
        filters: Object.keys(filters).length > 0 ? filters : (params.filters || {}),
        sort: params.sort || { field: 'created_at', order: 'desc' },
      };

      const data = await searchPublishAccounts(requestParams);

      if (data.status === 'success') {
        setAccountDataSource(data.items || []);
        setAccountPagination({
          current: data.page,
          pageSize: data.page_size,
          total: data.total,
        });
      } else {
        message.error('获取账号列表失败');
      }
    } catch (error) {
      console.error('搜索发布账号失败:', error);
      message.error('搜索发布账号失败，请稍后重试');
    } finally {
      setAccountLoading(false);
    }
  };

  // 处理账号筛选
  const handleAccountFilter = () => {
    // 重置到第一页
    setAccountPagination({
      ...accountPagination,
      current: 1,
    });
    loadPublishAccounts({ page: 1 });
  };

  const handlePublish = (record) => {
    setCurrentPublishRecord(record);
    setPublishAccountDrawerVisible(true);
    setSelectedAccountKeys([]);
    setSelectedAccounts([]);
    selectedAccountMapRef.current.clear();
    // 重置筛选表单
    accountFilterForm.resetFields();
    // 打开抽屉时加载账号列表
    loadPublishAccounts();
  };

  const loadDispatchAgents = async ({ silent = false } = {}) => {
    try {
      setDispatchAgentsLoading(true);
      const resp = await listAgents({
        page: 1,
        page_size: 100,
        online_window_sec: 120,
        user_id: String(user?.user_id || '').trim(),
      });
      if (resp?.status === 'success') {
        const items = Array.isArray(resp.items) ? resp.items : [];
        const onlineItems = items.filter((item) => item?.is_online === true);
        setDispatchAgents(onlineItems.length > 0 ? onlineItems : items);
      } else if (!silent) {
        message.error(resolveErrorMessage(resp?.error_message, '获取 Agent 列表失败'));
      }
    } catch (error) {
      if (!silent) message.error(resolveErrorMessage(error?.message || String(error), '获取 Agent 列表失败'));
    } finally {
      setDispatchAgentsLoading(false);
    }
  };

  // 处理账号选择确认
  const handleConfirmAccountSelection = () => {
    if (selectedAccountKeys.length === 0) {
      message.warning('请至少选择一个发布账号');
      return;
    }
    // 根据选中的 keys 获取完整的账号信息（支持跨页选择）
    const selected = selectedAccountKeys
      .map((accountId) => selectedAccountMapRef.current.get(accountId))
      .filter(Boolean);
    setSelectedAccounts(selected);
    // 关闭账号选择抽屉，打开发布时间选择抽屉
    setPublishAccountDrawerVisible(false);
    setPublishTimeDrawerVisible(true);
    // 重置表单
    publishTimeForm.resetFields();
    setPublishType('immediate');
    setDispatchMode('default');
    setPreferredAgentId('');
    loadDispatchAgents({ silent: true });
  };

  // 处理返回按钮
  const handleBackToAccountSelection = () => {
    if (publishSubmitting) return;
    setPublishTimeDrawerVisible(false);
    setPublishAccountDrawerVisible(true);
    setDispatchMode('default');
    setPreferredAgentId('');
    publishTimeForm.resetFields();
  };

  const buildAccountAgentView = (account) => {
    const readiness = account?.login_readiness && typeof account.login_readiness === 'object' ? account.login_readiness : {};
    const agentSummary = account?.agent_summary && typeof account.agent_summary === 'object' ? account.agent_summary : {};
    const platformCode = normalizePlatformCode(account?.platform_code || readiness?.platform_code || account?.platform);
    const executionMode = String(account?.execution_mode || readiness?.execution_mode || '').trim().toLowerCase();
    const authState = String(account?.auth_state || readiness?.auth_state || '').trim().toLowerCase();

    const agentRequired = readiness.agent_required === true
      || executionMode === 'local_agent'
      || LOCAL_AGENT_PLATFORM_CODES.has(platformCode);
    if (!agentRequired) return { state: 'cloud' };

    const agentBound = readiness.agent_bound === true || Boolean(String(account?.agent_id || '').trim());
    if (!agentBound) return { state: 'unbound' };

    const hasOnlineFlag = typeof readiness.agent_online === 'boolean' || typeof agentSummary?.is_online === 'boolean';
    const agentOnline = typeof readiness.agent_online === 'boolean'
      ? readiness.agent_online
      : (typeof agentSummary?.is_online === 'boolean'
        ? agentSummary.is_online
        : String(agentSummary?.status || '').trim().toLowerCase() === 'online');
    if (hasOnlineFlag && !agentOnline) return { state: 'offline' };

    const canPublish = readiness.can_publish === true || READY_AUTH_STATES.has(authState);
    if (canPublish) return { state: 'ready' };
    return { state: 'needs_login' };
  };

  // 处理确认发布
  const handleConfirmPublish = async () => {
    if (publishSubmitting) return;
    if (!currentPublishRecord) {
      message.error('发布内容信息缺失');
      return;
    }

    if (selectedAccounts.length === 0) {
      message.error('未选择发布账号');
      return;
    }
    const forbiddenAccounts = (selectedAccounts || []).filter((account) => account?.permission?.can_operate === false);
    if (forbiddenAccounts.length > 0) {
      message.error('所选账号包含无操作权限账号，请取消后重试');
      return;
    }

    let submitController = null;
    try {
      setPublishSubmitting(true);
      submitController = beginPublishSubmitRequest();
      const requestOptions = { signal: submitController.signal };
      const contentIds = [currentPublishRecord.content_id];
      const targetAccountIds = selectedAccounts.map(account => account.account_id);

      if (publishType === 'immediate') {
        const localTargetAccounts = selectedAccounts.filter((account) => (
          LOCAL_AGENT_PLATFORM_CODES.has(normalizePlatformCode(account?.platform_code || account?.platform))
        ));

        if (dispatchMode === 'preferred') {
          if (!preferredAgentId) {
            message.warning('请选择目标 Agent');
            return;
          }
          if (localTargetAccounts.length === 0) {
            message.warning('当前选择的账号中没有本地 Agent 平台账号，无法使用指定 Agent 发布');
            return;
          }
          const previewResp = await previewAgentDispatch(
            {
              account_ids: localTargetAccounts.map((item) => item.account_id).filter(Boolean),
              preferred_agent_id: preferredAgentId,
              online_window_sec: 120,
            },
            requestOptions,
          );
          if (previewResp?.status !== 'success') {
            message.error(resolveErrorMessage(previewResp?.error_message, '执行设备预检失败'));
            return;
          }
          const ineligible = (previewResp?.items || []).filter((item) => item?.eligible !== true);
          if (ineligible.length > 0) {
            const first = ineligible[0] || {};
            const reasons = Array.isArray(first?.reasons) ? first.reasons.join(', ') : 'unknown';
            message.error(`执行设备预检未通过：账号 ${first?.account_id || '-'}（${reasons}）`);
            return;
          }
        } else {
          const risk = selectedAccounts.reduce((acc, account) => {
            const view = buildAccountAgentView(account);
            if (view.state === 'cloud') return acc;
            acc.localRequired += 1;
            if (view.state === 'ready') acc.ready += 1;
            else if (view.state === 'unbound') acc.unbound += 1;
            else if (view.state === 'offline') acc.offline += 1;
            else acc.needsLogin += 1;
            return acc;
          }, { localRequired: 0, ready: 0, unbound: 0, offline: 0, needsLogin: 0 });

          if (risk.localRequired > 0 && (risk.unbound > 0 || risk.offline > 0 || risk.needsLogin > 0)) {
            message.warning(`立即发布已阻止：已选账号中存在 Agent 风险（未绑定 ${risk.unbound}，离线 ${risk.offline}，待登录 ${risk.needsLogin}）。请先处理账号状态后重试。`);
            return;
          }
        }

        message.loading({ content: '正在提交立即发布请求...', key: 'publish_submit', duration: 0 });
        // 立即发布
        const params = {
          content_ids: contentIds,
          target_account_ids: targetAccountIds,
          publisher_user_id: user?.user_id || user?.id || 'system',
          template_type: currentPublishRecord.content_type || '视频',
          override: {
            allow_sms_verify: true,
          },
          dispatch: dispatchMode === 'preferred'
            ? { mode: 'preferred', preferred_agent_id: preferredAgentId }
            : { mode: 'default' },
        };

        const response = await executePublishNow(params, requestOptions);

        if (response.status === 'success') {
          const contentId = currentPublishRecord.content_id;
          const notificationKey = `publish_now_${contentId}_${Date.now()}`;

          setOptimisticPublishingByContentId((prev) => ({
            ...(prev || {}),
            [contentId]: Date.now(),
          }));
          setDataSource((prev) => (prev || []).map((it) => (
            it?.content_id === contentId
              ? { ...it, status: it?.status === '待发布' ? '发布中' : it?.status }
              : it
          )));

          openPublishNotification({
            key: notificationKey,
            title: '已提交立即发布',
            contentId,
            description: (
              <div>
                <div>内容ID：{contentId}</div>
                <div>账号数：{targetAccountIds.length}</div>
                <div>正在等待发布记录生成…</div>
              </div>
            ),
          });
          startPollPublishRecord({ contentId, notificationKey });

          message.success({
            content: (
              <span>
                已提交立即发布，稍后可在“已发布内容”查看状态。
                <Button
                  type="link"
                  size="small"
                  style={{ padding: '0 4px' }}
                  onClick={() => navigate('/content?tab=published')}
                >
                  去查看
                </Button>
              </span>
            ),
            key: 'publish_submit',
            duration: 4,
          });
          // 关闭所有抽屉
          setPublishTimeDrawerVisible(false);
          setPublishAccountDrawerVisible(false);
          setCurrentPublishRecord(null);
          setSelectedAccounts([]);
          setSelectedAccountKeys([]);
          setDispatchMode('default');
          setPreferredAgentId('');
          publishTimeForm.resetFields();
          // 刷新内容列表（延迟一下，避免后端异步落库导致“看起来没变化”）
          setTimeout(() => { loadData().catch(() => {}); }, 800);
        } else {
          message.error({ content: response?.message || '发布失败', key: 'publish_submit' });
        }
      } else {
        // 定时发布
        const values = await publishTimeForm.validateFields();

        if (!values.scheduled_time) {
          message.warning('请选择发布时间');
          return;
        }

        // 将时间转换为 ISO 字符串格式
        const runAt = values.scheduled_time.toISOString();

        const creatorUserId = user?.wechat?.openid || user?.user_id || currentPublishRecord?.creator_user_id || 'aispire';
        const creatorUserName = user?.nickname || user?.wechat?.nickname || user?.name || null;
        const params = {
          task_name: values.task_name,
          task_type: 'cron',
          creator_user_id: creatorUserId,
          creator_user_name: creatorUserName || undefined,
          status: 'active',
          max_items_per_account_per_run: values.max_items_per_account_per_run || 1,
          schedule: {
            trigger_type: 'cron',
            cron_expression: '0 3 * * *', // 对于单次定时任务，可以设置一个默认值，主要使用 run_at
            run_at: runAt,
            timezone: 'Asia/Shanghai',
          },
          filters: {
            content_types: [currentPublishRecord.content_type || '视频'],
            is_manual_reviewed: currentPublishRecord.is_manual_reviewed !== undefined ? currentPublishRecord.is_manual_reviewed : false,
          },
          target_platforms: [...new Set(selectedAccounts.map(account => account.platform))],
          target_account_ids: targetAccountIds,
        };

        const response = await createPublishTask(params, requestOptions);

        if (response.status === 'success') {
          message.success({ content: '定时发布任务已创建', key: 'publish_submit' });
          // 关闭所有抽屉
          setPublishTimeDrawerVisible(false);
          setPublishAccountDrawerVisible(false);
          setCurrentPublishRecord(null);
          setSelectedAccounts([]);
          setSelectedAccountKeys([]);
          publishTimeForm.resetFields();
          setPublishType('immediate');
          setDispatchMode('default');
          setPreferredAgentId('');
          // 刷新内容列表
          await loadData();
        } else {
          message.error({ content: response?.message || '创建定时发布任务失败', key: 'publish_submit' });
        }
      }
    } catch (error) {
      if (isAbortError(error)) {
        message.info({ content: '已取消本次发布请求', key: 'publish_submit' });
        return;
      }
      console.error('发布失败:', error);
      if (error.errorFields) {
        // 表单验证错误
        return;
      }
      message.error({ content: '发布失败，请稍后重试', key: 'publish_submit' });
    } finally {
      if (submitController && publishSubmitAbortRef.current === submitController) {
        publishSubmitAbortRef.current = null;
      }
      setPublishSubmitting(false);
    }
  };

  const handleDownload = async (record) => {
    try {
      message.loading({ content: '正在下载...', key: 'download', duration: 0 });

      const response = await downloadContent(record.content_id);

      // 如果返回的是 JSON 响应且包含下载链接
      if (response && response.download_url) {
        // 打开下载链接
        window.open(response.download_url, '_blank');
        message.success({ content: '下载成功', key: 'download' });
      } else if (response && response.status === 'success') {
        // 文件流下载已经在 API 中处理
        message.success({ content: '下载成功', key: 'download' });
      } else {
        message.error({ content: '下载失败', key: 'download' });
      }

      // 如果父组件有 onDownload 回调，也调用它
      if (onDownload) {
        onDownload(record);
      }
    } catch (error) {
      console.error('下载失败:', error);
      message.error({ content: '下载失败，请稍后重试', key: 'download' });
    }
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的内容');
      return;
    }

    const contentIds = [...selectedRowKeys];
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 条内容吗？`,
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await deleteContent({
            content_ids: contentIds,
          });

          if (response.status === 'success') {
            // 检查删除结果
            if (response.deleted_ids && response.deleted_ids.length > 0) {
              message.success(`成功删除 ${response.deleted_ids.length} 条内容`);
              // 清空选中项
              setSelectedRowKeys([]);
              // 刷新数据
              await reloadListAfterDelete(contentIds);
              // 如果父组件有 onDelete 回调，也调用它
              if (onDelete) {
                contentIds.forEach(id => onDelete(id));
              }
              return Promise.resolve();
            } else if (response.not_found_ids && response.not_found_ids.length > 0) {
              message.warning(`有 ${response.not_found_ids.length} 条内容不存在或已被删除`);
              // 即使未找到，也刷新列表
              await reloadListAfterDelete(contentIds);
              return Promise.resolve();
            } else {
              message.warning('删除失败');
              return Promise.reject(new Error('删除失败'));
            }
          } else {
            message.error('删除失败');
            return Promise.reject(new Error('删除失败'));
          }
        } catch (error) {
          console.error('批量删除失败:', error);
          message.error('批量删除失败，请稍后重试');
          return Promise.reject(error);
        }
      },
    });
  };

  // 批量下载
  const handleBatchDownload = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要下载的内容');
      return;
    }

    try {
      message.loading({ content: `正在下载 ${selectedRowKeys.length} 个文件...`, key: 'batchDownload', duration: 0 });

      let successCount = 0;
      let failCount = 0;

      // 循环下载每个文件，添加延迟避免浏览器阻止多个下载
      for (let i = 0; i < selectedRowKeys.length; i++) {
        const contentId = selectedRowKeys[i];
        try {
          // 添加延迟，避免浏览器阻止多个下载
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }

          const response = await downloadContent(contentId);

          if (response && (response.download_url || response.status === 'success')) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`下载内容 ${contentId} 失败:`, error);
          failCount++;
        }
      }

      message.destroy('batchDownload');

      if (successCount > 0 && failCount === 0) {
        message.success(`成功下载 ${successCount} 个文件`);
        // 批量下载成功后清空选中项
        setSelectedRowKeys([]);
      } else if (successCount > 0 && failCount > 0) {
        message.warning(`成功下载 ${successCount} 个文件，失败 ${failCount} 个`);
        // 部分成功时也清空选中项
        setSelectedRowKeys([]);
      } else {
        message.error('批量下载失败');
      }
    } catch (error) {
      console.error('批量下载失败:', error);
      message.destroy('batchDownload');
      message.error('批量下载失败，请稍后重试');
    }
  };

  // 处理检索按钮点击
  const handleSearch = async () => {
    await loadSearchData(pagination.current, pagination.pageSize);
  };

  const handleFilterFinish = (values) => {
    const {
      tags,
      ingested_at_range,
      ...restValues
    } = values;

    const payload = {
      ...restValues,
      // 标签：输入框中用逗号分隔，转换为数组
      tags: tags
        ? tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
        : undefined,
      // 入库时间：DatePicker.RangePicker，转换为后端需要的 from/to 结构
      // dayjs 对象通过 toDate() 转换为 Date 对象，然后使用 toISOString()
      ingested_at_range: ingested_at_range
        ? {
          from: (ingested_at_range[0]?.toDate ? ingested_at_range[0].toDate() : ingested_at_range[0]).toISOString(),
          to: (ingested_at_range[1]?.toDate ? ingested_at_range[1].toDate() : ingested_at_range[1]).toISOString(),
        }
        : undefined,
    };

    if (onFilter) {
      onFilter(payload);
    } else {
      // 如果父组件还没接 onFilter，这里先打印出来方便调试
      // 每个字段的名称和值结构：
      // content_type: ["视频", "图文"]
      // creator_user_id: "u123"
      // author: "作者"
      // status: ["待发布", "已发布"]
      // source: "自建"
      // tags: ["tag1", "tag2"]
      // available_platforms: ["微信视频号", "小红书"]
      // risk_flag: boolean
      // manual_review_required: boolean
      // is_manual_reviewed: boolean
      // ingested_at_range: { from: "...", to: "..." }
      // content_id: "内容ID"
      // is_manual_reviewed: boolean
      console.log('筛选条件：', payload);
    }
  };

  // 处理上传内容
  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('请至少选择一个文件');
      return;
    }

    try {
      const values = await uploadForm.validateFields();
      setUploading(true);

      const items = uploadItems || [];
      if (items.length === 0) {
        message.warning('请至少选择一个文件');
        return;
      }

      let okCount = 0;
      const failed = [];

      for (const it of items) {
        const f = it?.file?.originFileObj || it?.file;
        if (!f) continue;

        const formData = new FormData();
        if (values.source) formData.append('source', values.source);
        if (values.available_platforms && values.available_platforms.length > 0) {
          formData.append('available_platforms', values.available_platforms.join(','));
        }
        if (values.status) formData.append('status', values.status);

        if (it.title) formData.append('title', it.title);
        if (it.summary) formData.append('summary', it.summary);
        if (Array.isArray(it.tags) && it.tags.length > 0) {
          formData.append('tags', it.tags.join(','));
        }

        // 已经在预生成阶段做过 ASR/OCR+LLM；上传阶段不再自动生成，避免重复耗时。
        formData.append('auto_generate', 'false');

        formData.append('files', f);

        try {
          const resp = await uploadContent(formData);
          if (resp?.status === 'success') {
            okCount += 1;
          } else {
            failed.push({ name: it.name, error: resolveErrorMessage(resp?.message, '上传失败') });
          }
        } catch (e) {
          failed.push({ name: it.name, error: e?.message || String(e) });
        }
      }

      if (failed.length === 0) {
        message.success(`成功上传 ${okCount} 个文件`);
        setUploadModalVisible(false);
        uploadForm.resetFields();
        setFileList([]);
        setUploadItems([]);
        setSelectedUploadUid(null);
        uploadItemForm.resetFields();
        await loadData();
      } else {
        message.warning(`上传完成：成功 ${okCount}，失败 ${failed.length}（请查看控制台详情）`);
        // eslint-disable-next-line no-console
        console.error('上传失败列表:', failed);
        await loadData();
      }
    } catch (error) {
      console.error('上传失败:', error);
      if (error.errorFields) {
        // 表单验证错误
        return;
      }
      message.error('上传失败，请稍后重试');
    } finally {
      setUploading(false);
    }
  };


  const _revokeXhsNotePreviewUrl = (uid) => {
    try {
      const m = xhsNotePreviewUrlByUidRef.current;
      const url = m?.get(uid);
      if (url) {
        URL.revokeObjectURL(url);
        m.delete(uid);
      }
    } catch (_) {
      // ignore
    }
  };

  const _getXhsNotePreviewUrl = (file) => {
    try {
      const uid = file?.uid;
      if (!uid) return '';
      const m = xhsNotePreviewUrlByUidRef.current;
      const cached = m?.get(uid);
      if (cached) return cached;
      const f = file?.originFileObj || file;
      if (!f) return '';
      const url = URL.createObjectURL(f);
      m.set(uid, url);
      return url;
    } catch (_) {
      return '';
    }
  };

  const handleXhsNoteImagesChange = ({ fileList: newFileList }) => {
    const next = Array.isArray(newFileList) ? newFileList.slice(0, 18) : [];
    if ((newFileList || []).length > 18) {
      message.warning('最多支持 18 张图片，已自动截断');
    }
    setXhsNoteImageFileList(next);
  };

  const handleXhsNoteRemoveImage = (uid) => {
    setXhsNoteImageFileList((prev) => (prev || []).filter((f) => f.uid !== uid));
    _revokeXhsNotePreviewUrl(uid);
    if (xhsNoteDragUid === uid) setXhsNoteDragUid(null);
    if (xhsNoteDropHint?.uid === uid) setXhsNoteDropHint(null);
  };

  const _readXhsNoteDragUid = (e) => {
    try {
      return (
        e?.dataTransfer?.getData('application/xhsnote-image-uid') ||
        e?.dataTransfer?.getData('text/plain') ||
        null
      );
    } catch (_) {
      return null;
    }
  };

  const handleXhsNoteDropOn = (targetUid, position = 'before', fromUidOverride = null) => {
    const fromUid = fromUidOverride || xhsNoteDragUid;
    setXhsNoteDragUid(null);
    setXhsNoteDropHint(null);
    if (!fromUid || !targetUid || fromUid === targetUid) return;
    setXhsNoteImageFileList((prev) => {
      const list = [...(prev || [])];
      const fromIdx = list.findIndex((x) => x.uid === fromUid);
      const toIdx = list.findIndex((x) => x.uid === targetUid);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const [moved] = list.splice(fromIdx, 1);
      const adjustedToIdx = fromIdx < toIdx ? toIdx - 1 : toIdx;
      const insertAt = position === 'after' ? adjustedToIdx + 1 : adjustedToIdx;
      list.splice(Math.max(0, Math.min(insertAt, list.length)), 0, moved);
      return list;
    });
  };

  const handleXhsNoteDropToEnd = (fromUidOverride = null) => {
    const fromUid = fromUidOverride || xhsNoteDragUid;
    setXhsNoteDragUid(null);
    setXhsNoteDropHint(null);
    if (!fromUid) return;
    setXhsNoteImageFileList((prev) => {
      const list = [...(prev || [])];
      const fromIdx = list.findIndex((x) => x.uid === fromUid);
      if (fromIdx < 0) return prev;
      const [moved] = list.splice(fromIdx, 1);
      list.push(moved);
      return list;
    });
  };

  const handleGenerateXhsNoteSummary = async () => {
    try {
      const v = xhsNoteForm.getFieldsValue(true) || {};
      const body = String(v.body || '').trim();
      const title = String(v.title || '').trim();
      if (!body) {
        message.warning('请先填写正文');
        return;
      }
      setXhsNoteSummaryGenerating(true);
      const resp = await previewXhsNoteSummary({ title, body });
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.message, '生成失败'));
      }
      const summary = String(resp?.summary || '').trim();
      xhsNoteForm.setFieldsValue({ summary });
      message.success('已生成简介');
    } catch (e) {
      message.error(e?.message || String(e));
    } finally {
      setXhsNoteSummaryGenerating(false);
    }
  };

  const handleGenerateXhsNoteTags = async () => {
    try {
      const v = xhsNoteForm.getFieldsValue(true) || {};
      const body = String(v.body || '').trim();
      const title = String(v.title || '').trim();
      const summary = String(v.summary || '').trim();
      if (!body) {
        message.warning('请先填写正文');
        return;
      }
      setXhsNoteTagsGenerating(true);
      const resp = await previewXhsNoteTags({ title, body, summary });
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.message, '生成失败'));
      }
      const tags = Array.isArray(resp?.tags) ? resp.tags : [];
      xhsNoteForm.setFieldsValue({ tags });
      message.success('已生成标签');
    } catch (e) {
      message.error(e?.message || String(e));
    } finally {
      setXhsNoteTagsGenerating(false);
    }
  };

  const resetXhsNoteModal = () => {
    try {
      for (const f of xhsNoteImageFileList || []) {
        if (f?.uid) _revokeXhsNotePreviewUrl(f.uid);
      }
    } catch (_) {
      // ignore
    }
    setXhsNoteImageFileList([]);
    setXhsNoteDragUid(null);
    setXhsNoteDropHint(null);
    xhsNoteForm.resetFields();
  };

  const handleCreateXhsNote = async () => {
    if (xhsNoteCreating) return;
    try {
      const values = await xhsNoteForm.validateFields();
      if (!xhsNoteImageFileList || xhsNoteImageFileList.length === 0) {
        message.warning('请至少上传 1 张图片');
        return;
      }
      setXhsNoteCreating(true);

      const fd = new FormData();
      fd.append('title', String(values.title || '').trim());
      fd.append('body', String(values.body || '').trim());
      if (String(values.summary || '').trim()) {
        fd.append('summary', String(values.summary || '').trim());
      }
      if (Array.isArray(values.tags) && values.tags.length > 0) {
        fd.append('tags', values.tags.join(','));
      }

      // Keep image order as shown in UI.
      for (const f of xhsNoteImageFileList) {
        const fileObj = f?.originFileObj || f;
        if (fileObj) fd.append('images', fileObj);
      }

      const resp = await createXhsNote(fd);
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.message, '创建失败'));
      }

      message.success('已创建小红书笔记内容');
      setXhsNoteModalVisible(false);
      resetXhsNoteModal();
      await loadData();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || String(e));
    } finally {
      setXhsNoteCreating(false);
    }
  };

  // 处理文件列表变化
  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);

    // If uploading videos, set a sensible default for delivery platforms (only when user hasn't chosen yet).
    try {
      const isVideoName = (name) => {
        const n = String(name || '').toLowerCase();
        return /\.(mp4|avi|mov|wmv|flv|mkv)$/.test(n);
      };
      const currentPlatforms = uploadForm.getFieldValue('available_platforms');
      const emptyPlatforms = !Array.isArray(currentPlatforms) || currentPlatforms.length === 0;
      const onlyVideos = (newFileList || []).length > 0 && (newFileList || []).every((f) => isVideoName(f?.name));
      if (emptyPlatforms && onlyVideos) {
        uploadForm.setFieldsValue({
          available_platforms: ['小红书', '抖音', '快手', '微信视频号', '百家号', 'B站'],
        });
      }
    } catch (_) {
      // best-effort
    }

    setUploadItems((prev) => {
      const prevByUid = new Map((prev || []).map((it) => [it.uid, it]));
      const next = (newFileList || []).map((f) => {
        const uid = f.uid;
        const existed = prevByUid.get(uid);
        const name = f.name || existed?.name || '';
        const base = name.replace(/\.[^.]+$/, '');
        return {
          uid,
          file: f,
          name,
          title: existed?.title ?? base,
          summary: existed?.summary ?? '',
          tags: existed?.tags ?? [],
          text_source: existed?.text_source ?? 'asr', // asr|image|auto
          ocr_max_frames: existed?.ocr_max_frames ?? 6,
          text_source_customized: existed?.text_source_customized ?? false,
          aiStatus: existed?.aiStatus ?? 'idle', // idle|loading|done|error
          aiError: existed?.aiError ?? '',
        };
      });
      // keep selection stable
      if (next.length > 0) {
        const still = next.some((it) => it.uid === selectedUploadUid);
        if (!still) {
          setSelectedUploadUid(next[0].uid);
          const first = next[0];
          suppressUploadItemSyncRef.current = true;
          uploadItemForm.setFieldsValue({
            title: first.title,
            summary: first.summary,
            tags: first.tags,
            text_source: first.text_source,
            ocr_max_frames: first.ocr_max_frames,
          });
          suppressUploadItemSyncRef.current = false;
        }
      } else {
        setSelectedUploadUid(null);
        uploadItemForm.resetFields();
      }
      return next;
    });
  };

  // 处理文件移除
  const handleRemove = (file) => {
    const newFileList = fileList.filter(item => item.uid !== file.uid);
    setFileList(newFileList);
    setUploadItems((prev) => (prev || []).filter((it) => it.uid !== file.uid));
    if (selectedUploadUid === file.uid) {
      setSelectedUploadUid(null);
      uploadItemForm.resetFields();
    }
  };

  const selectedUploadItem = useMemo(
    () => (uploadItems || []).find((it) => it.uid === selectedUploadUid) || null,
    [selectedUploadUid, uploadItems]
  );

  // Keep the right-side form in sync with the currently selected file even when batch generation updates items.
  const selectedUploadItemSyncKey = useMemo(() => {
    if (!selectedUploadItem) return '';
    const tagsKey = Array.isArray(selectedUploadItem.tags) ? selectedUploadItem.tags.join(',') : '';
    return [
      selectedUploadItem.uid,
      selectedUploadItem.title || '',
      selectedUploadItem.summary || '',
      tagsKey,
      selectedUploadItem.text_source || '',
      String(selectedUploadItem.ocr_max_frames ?? ''),
      selectedUploadItem.aiStatus || '',
      selectedUploadItem.aiError || '',
    ].join('|');
  }, [selectedUploadItem]);

  useEffect(() => {
    if (!selectedUploadItem) return;
    suppressUploadItemSyncRef.current = true;
    uploadItemForm.setFieldsValue({
      title: selectedUploadItem.title,
      summary: selectedUploadItem.summary,
      tags: selectedUploadItem.tags,
      text_source: selectedUploadItem.text_source,
      ocr_max_frames: selectedUploadItem.ocr_max_frames,
    });
    suppressUploadItemSyncRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUploadItemSyncKey]);

  const selectUploadItem = (uid) => {
    setSelectedUploadUid(uid);
    const it = (uploadItems || []).find((x) => x.uid === uid);
    if (it) {
      suppressUploadItemSyncRef.current = true;
      uploadItemForm.setFieldsValue({
        title: it.title,
        summary: it.summary,
        tags: it.tags,
        text_source: it.text_source,
        ocr_max_frames: it.ocr_max_frames,
      });
      suppressUploadItemSyncRef.current = false;
    }
  };

  const syncSelectedItemFromForm = () => {
    if (!selectedUploadUid) return;
    if (suppressUploadItemSyncRef.current) return;
    const v = uploadItemForm.getFieldsValue(true) || {};
    setUploadItems((prev) =>
      (prev || []).map((it) =>
        it.uid === selectedUploadUid
          ? {
            ...it,
            title: v.title || '',
            summary: v.summary || '',
            tags: Array.isArray(v.tags) ? v.tags : [],
            text_source: v.text_source || 'asr',
            ocr_max_frames: v.ocr_max_frames ?? it.ocr_max_frames ?? 6,
          }
          : it
      )
    );
  };

  const applyGlobalTextSourceToUntouched = () => {
    if (!uploadItems || uploadItems.length === 0) return;
    setUploadItems((prev) => {
      const next = (prev || []).map((it) => {
        if (it?.text_source_customized) return it;
        return { ...it, text_source: globalTextSource, ocr_max_frames: globalOcrMaxFrames };
      });
      const selected = next.find((it) => it.uid === selectedUploadUid);
      if (selected && !selected.text_source_customized) {
        uploadItemForm.setFieldsValue({
          text_source: selected.text_source,
          ocr_max_frames: selected.ocr_max_frames,
        });
      }
      return next;
    });
    message.success('已应用到未修改的文件');
  };

  const runPreviewForItem = async (it, applyMode = 'all') => {
    const f = it?.file?.originFileObj || it?.file;
    if (!f) throw new Error('文件缺失');

    const fd = new FormData();
    fd.append('file', f);
    const chosenTextSource = String(it?.text_source || 'asr').trim().toLowerCase();
    fd.append('text_source', chosenTextSource);
    if (chosenTextSource === 'image' || chosenTextSource === 'auto') {
      const frames = Number(it?.ocr_max_frames ?? 6);
      if (Number.isFinite(frames) && frames > 0) {
        fd.append('ocr_max_frames', String(Math.floor(frames)));
      }
    }

    const resp = await previewContentMetadata(fd);
    if (resp?.status !== 'success') {
      throw new Error(resolveErrorMessage(resp?.message, 'AI生成失败'));
    }
    const nextTitle = String(resp?.title || '').trim();
    const nextSummary = String(resp?.summary || '').trim();
    const nextTags = Array.isArray(resp?.tags) ? resp.tags : [];
    return {
      title: applyMode === 'title' ? (nextTitle || it.title) : (nextTitle || it.title),
      summary: applyMode === 'title' ? it.summary : (nextSummary || it.summary),
      tags: applyMode === 'title' ? it.tags : (nextTags.length > 0 ? nextTags : it.tags),
    };
  };

	  const runBatchGenerate = async ({ retryOnlyFailures = false } = {}) => {
	    if (previewing || batchGenerating) return;
	    const items = uploadItems || [];
	    const targets = retryOnlyFailures
	      ? items.filter((it) => it.aiStatus === 'error')
	      : (batchSkipDone ? items.filter((it) => it.aiStatus !== 'done') : items);

    if (targets.length === 0) {
      message.info(retryOnlyFailures ? '没有需要重试的失败项' : '没有需要生成的内容');
      return;
    }

	    setBatchGenerating(true);
	    setBatchFailures([]);
	    setBatchProgress({ total: targets.length, done: 0, ok: 0, failed: 0 });

	    // Mark all targets as loading
	    const targetUidSet = new Set(targets.map((t) => t.uid));
	    setUploadItems((prev) =>
	      (prev || []).map((it) => (targetUidSet.has(it.uid) ? { ...it, aiStatus: 'loading', aiError: '' } : it))
	    );

	    const limit = 3;
	    let nextIndex = 0;
	    let okCount = 0;
	    let failedCount = 0;

	    const worker = async () => {
	      while (true) {
	        const idx = nextIndex;
	        nextIndex += 1;
	        if (idx >= targets.length) return;
	        const it = targets[idx];
	        try {
	          const patch = await runPreviewForItem(it, 'all');
	          setUploadItems((prev) =>
	            (prev || []).map((x) => (x.uid === it.uid ? { ...x, ...patch, aiStatus: 'done', aiError: '' } : x))
	          );
	          if (selectedUploadUid === it.uid) {
	            uploadItemForm.setFieldsValue({
	              title: patch.title,
	              summary: patch.summary,
	              tags: patch.tags,
	            });
	          }
	          okCount += 1;
	          setBatchProgress((p) => ({ ...p, done: p.done + 1, ok: p.ok + 1 }));
	        } catch (e) {
	          const errMsg = e?.message || String(e);
	          setUploadItems((prev) =>
	            (prev || []).map((x) => (x.uid === it.uid ? { ...x, aiStatus: 'error', aiError: errMsg } : x))
	          );
	          failedCount += 1;
	          setBatchFailures((prev) => [...(prev || []), { uid: it.uid, name: it.name, error: errMsg }]);
	          setBatchProgress((p) => ({ ...p, done: p.done + 1, failed: p.failed + 1 }));
	        }
	      }
	    };

	    await Promise.all(Array.from({ length: Math.min(limit, targets.length) }, () => worker()));
	    setBatchGenerating(false);
	    const total = targets.length;
	    if (failedCount === 0) {
	      message.success(`批量生成完成：成功 ${okCount}/${total}`);
	    } else {
	      message.warning(`批量生成完成：成功 ${okCount}/${total}，失败 ${failedCount}（见失败列表）`);
	    }
	  };

  const doPreviewForSelected = async (applyMode) => {
    if (!selectedUploadItem) return;
    const targetUid = selectedUploadItem.uid;
    const existing = uploadItemForm.getFieldsValue(true) || {};
    const f = selectedUploadItem?.file?.originFileObj || selectedUploadItem?.file;
    if (!f) return;
    try {
      setPreviewingAction(applyMode);
      setPreviewing(true);
      setUploadItems((prev) =>
        (prev || []).map((it) => (it.uid === targetUid ? { ...it, aiStatus: 'loading', aiError: '' } : it))
      );

      const fd = new FormData();
      fd.append('file', f);
      const chosenTextSource = String(existing?.text_source || selectedUploadItem.text_source || 'asr').trim().toLowerCase();
      fd.append('text_source', chosenTextSource);
      if (chosenTextSource === 'image' || chosenTextSource === 'auto') {
        const frames = Number(existing?.ocr_max_frames ?? selectedUploadItem.ocr_max_frames ?? 6);
        if (Number.isFinite(frames) && frames > 0) {
          fd.append('ocr_max_frames', String(Math.floor(frames)));
        }
      }

      const resp = await previewContentMetadata(fd);
      if (resp?.status !== 'success') {
        throw new Error(resolveErrorMessage(resp?.message, 'AI生成失败'));
      }

      const nextTitle = String(resp?.title || '').trim();
      const nextSummary = String(resp?.summary || '').trim();
      const nextTags = Array.isArray(resp?.tags) ? resp.tags : [];

      const existingTitle = String(existing?.title || '').trim();
      const existingSummary = String(existing?.summary || '');
      const existingTags = Array.isArray(existing?.tags) ? existing.tags : null;

      const fallbackTitle = existingTitle || selectedUploadItem.title || '';
      const fallbackSummary = existingSummary || selectedUploadItem.summary || '';
      const fallbackTags = existingTags || selectedUploadItem.tags || [];

      const mergedTitle = String(nextTitle || fallbackTitle).trim();
      const mergedSummary = applyMode === 'title' ? fallbackSummary : String(nextSummary || fallbackSummary);
      const mergedTags = applyMode === 'title' ? fallbackTags : (nextTags.length > 0 ? nextTags : fallbackTags);

      setUploadItems((prev) =>
        (prev || []).map((it) => {
          if (it.uid !== targetUid) return it;
          return {
            ...it,
            aiStatus: 'done',
            aiError: '',
            title: mergedTitle || it.title,
            summary: mergedSummary,
            tags: mergedTags,
          };
        })
      );

      // update form view
      if (selectedUploadUid === targetUid) {
        uploadItemForm.setFieldsValue({ title: mergedTitle, summary: mergedSummary, tags: mergedTags });
      }
    } catch (e) {
      setUploadItems((prev) =>
        (prev || []).map((it) =>
          it.uid === targetUid ? { ...it, aiStatus: 'error', aiError: e?.message || String(e) } : it
        )
      );
      message.error(e?.message || String(e));
    } finally {
      setPreviewing(false);
      setPreviewingAction(null);
    }
  };


  const _normalizeTagsArray = (tags) => {
    if (!tags) return [];
    if (Array.isArray(tags)) {
      return tags.map((t) => String(t || '').trim()).filter(Boolean);
    }
    const s = String(tags || '').trim();
    if (!s) return [];
    return s.split(/[,，]/).map((t) => t.trim()).filter(Boolean);
  };

  // 处理内容id点击，打开抽屉
  const handleContentIdClick = async (contentId, initialTab = 'preview') => {
    setDrawerVisible(true);
    setDetailLoading(true);
    setActiveTab(initialTab);
    try {
      const response = await getContentDetail({ content_id: contentId });
      if (response.status === 'success' && response.data) {
        setContentDetail(response.data);
        const bodyText = response.data.body ?? response.data.content ?? '';
        // 设置表单初始值
        detailForm.setFieldsValue({
          title: response.data.title,
          summary: response.data.summary,
          body: bodyText,
          tags: _normalizeTagsArray(response.data.tags),
          source: response.data.source,
          content_type: response.data.content_type,
          creator_user_id: response.data.creator_user_id,
          risk_flag: response.data.risk_flag,
          status: response.data.status,
          available_platforms: response.data.available_platforms || [],
          manual_review_required: response.data.manual_review_required,
          is_manual_reviewed: response.data.is_manual_reviewed,
          primary_category: response.data.primary_category,
          secondary_category: response.data.secondary_category,
        });
      } else {
        message.error('获取内容详情失败');
        setDrawerVisible(false);
      }
    } catch (error) {
      console.error('获取内容详情失败:', error);
      message.error('获取内容详情失败，请稍后重试');
      setDrawerVisible(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const renderWrapText = (value) => (
    <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
      {value ? String(value) : '-'}
    </div>
  );

  const SUMMARY_TOOLTIP_WIDTH = 800;
  const SUMMARY_TOOLTIP_MAX_WIDTH = 'min(800px, calc(100vw - 24px))';
  const SUMMARY_TOOLTIP_MAX_HEIGHT = '60vh';

  const renderSummaryWithTooltip = (value) => {
    const text = value ? String(value) : '';
    if (!text) return '-';
    const needsTruncate = text.length > 100;
    const display = needsTruncate ? `${text.slice(0, 100)}…` : text;
    const content = (
      <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
        {display}
      </div>
    );
    if (!needsTruncate) return content;
    return (
      <Tooltip
        title={<div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</div>}
        placement="top"
        arrow={{ pointAtCenter: true }}
        autoAdjustOverflow={{ adjustX: 1, adjustY: 1 }}
        styles={{
          container: {
            width: SUMMARY_TOOLTIP_WIDTH,
            maxWidth: SUMMARY_TOOLTIP_MAX_WIDTH,
            maxHeight: SUMMARY_TOOLTIP_MAX_HEIGHT,
            overflowY: 'auto',
          },
        }}
        overlayInnerStyle={{
          width: SUMMARY_TOOLTIP_WIDTH,
          maxWidth: SUMMARY_TOOLTIP_MAX_WIDTH,
          maxHeight: SUMMARY_TOOLTIP_MAX_HEIGHT,
          overflowY: 'auto',
        }}
      >
        {content}
      </Tooltip>
    );
  };

  const isVideoType = (value) => {
    if (!value) return false;
    const text = String(value).trim();
    const normalized = text.toLowerCase();
    return text === '视频' || normalized === 'video';
  };

  const getUrlPathname = (url) => {
    const raw = String(url || '').trim();
    if (!raw) return '';
    try {
      // Use base to support relative URLs
      return new URL(raw, window.location.origin).pathname || raw;
    } catch (_) {
      return raw;
    }
  };

  const isVideoUrl = (url) => {
    const path = getUrlPathname(url);
    return /\.(mp4|avi|mov|wmv|flv|webm|m3u8)$/i.test(path);
  };

  const isImageUrl = (url) => {
    const path = getUrlPathname(url);
    return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(path);
  };

  const columns = [
    {
      title: '内容id',
      dataIndex: 'content_id',
      key: 'content_id',
      width: 120,
      render: (text) => (
        <Button
          type="link"
          size="small"
          onClick={() => handleContentIdClick(text, 'preview')}
          block
          style={{
            padding: 0,
            height: 'auto',
            textAlign: 'left',
            whiteSpace: 'normal',
            wordBreak: 'break-all',
          }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: '内容来源',
      dataIndex: 'source',
      key: 'source',
      width: 120,
    },
    {
      title: '内容类型',
      dataIndex: 'content_type',
      key: 'content_type',
      width: 120,
      render: (contentType) => {
        if (!contentType) return '-';
        const typeText = String(contentType);
        const normalized = typeText.toLowerCase();

        const mapped =
          normalized === 'video' ? '视频'
            : (normalized === 'image_text' || normalized === 'imagetext' || normalized === 'article') ? '图文'
              : typeText;

        return <span style={{ color: '#333' }}>{mapped}</span>;
      },
    },
    {
      title: '创作者',
      dataIndex: 'author',
      key: 'author',
      width: 140,
      render: (value, record) => {
        const authorText = value ? String(fixMojibakeUtf8(value)).trim() : '';
        const creatorId = record?.creator_user_id ? String(record.creator_user_id).trim() : '';
        const display = authorText || creatorId || '-';
        if (display === '-') return '-';
        const title = authorText && creatorId ? `${authorText}\n${creatorId}` : display;
        return <span title={title}>{display}</span>;
      },
    },
    {
      title: '创作时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => formatDateTime(text),
      sorter: (a, b) => {
        if (!a.created_at || !b.created_at) return 0;
        return new Date(a.created_at) - new Date(b.created_at);
      },
    },
    {
      title: '入库时间',
      dataIndex: 'ingested_at',
      key: 'ingested_at',
      width: 180,
      render: (text) => formatDateTime(text),
      sorter: (a, b) => {
        if (!a.ingested_at || !b.ingested_at) return 0;
        return new Date(a.ingested_at) - new Date(b.ingested_at);
      },
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      render: renderWrapText,
    },
    {
      title: '内容简介',
      dataIndex: 'summary',
      key: 'summary',
      width: 240,
      render: renderSummaryWithTooltip,
    },
    {
      title: '一级分类',
      dataIndex: 'category_level1',
      key: 'category_level1',
      width: 120,
    },
    {
      title: '二级分类',
      dataIndex: 'category_level2',
      key: 'category_level2',
      width: 120,
    },
    {
      title: '内容标签',
      dataIndex: 'tags',
      key: 'tags',
      width: 150,
      render: (tags) => {
        if (Array.isArray(tags)) {
          return tags.map((tag, index) => <Tag key={index}>{tag}</Tag>);
        }
        return tags;
      },
    },
    {
      title: '风险状态',
      dataIndex: 'risk_flag',
      key: 'risk_flag',
      width: 100,
      render: (value) => value ? '高风险' : '低风险',
    },
    {
      title: '是否需要人工审核',
      dataIndex: 'manual_review_required',
      key: 'manual_review_required',
      width: 100,
      render: (value) => value ? '是' : '否',
    },
    {
      title: '是否审核',
      dataIndex: 'is_manual_reviewed',
      key: 'is_manual_reviewed',
      width: 100,
      render: (value) => value ? '是' : '否',
    },
    {
      title: '内容状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => getStatusTag(status),
    },
    {
      title: '投递平台',
      dataIndex: 'available_platforms',
      key: 'available_platforms',
      width: 200,
      render: (platforms) => {
        if (Array.isArray(platforms)) {
          return platforms.join(', ');
        }
        return platforms;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 300,
      fixed: 'right',
      render: (_, record) => (
        <Space size={[8, 0]} wrap>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleContentIdClick(record.content_id, 'edit')}
            style={{ padding: '0 4px' }}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            icon={<CloudUploadOutlined />}
            onClick={() => handlePublish(record)}
            style={{ padding: '0 4px' }}
          >
            发布
          </Button>
          {isVideoType(record.content_type) && (
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
              style={{ padding: '0 4px' }}
            >
              下载
            </Button>
          )}
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.content_id)}
            style={{ padding: '0 4px' }}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-panel">
      <div className="page-panel__top">
        <Form
          form={form}
          layout="horizontal"
          onFinish={handleFilterFinish}
          style={{ marginBottom: 0 }}
        >
        <Row gutter={[16, 16]} justify="space-between">
          <Col span={18}>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Form.Item label="内容来源" name="source" style={{ marginBottom: 0 }}>
                  <Select
                    style={{ width: '100%' }}
                    allowClear
                    options={[
                      { label: '自建', value: '自建' },
                      { label: '抓取', value: '抓取' },
                      { label: 'AI生成', value: 'AI生成' },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item
                  label="标签"
                  name="tags"
                  style={{ marginBottom: 0 }}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) {
                          return Promise.resolve();
                        }
                        if (String(value).includes('，')) {
                          return Promise.reject(new Error('标签分隔符请使用英文逗号","，不能使用中文逗号","'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="多个标签用逗号分隔，例如：tag1,tag2" style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="是否审核" name="is_manual_reviewed" style={{ marginBottom: 0 }}>
                  <Select
                    style={{ width: '100%' }}
                    allowClear
                    options={[
                      { label: '是', value: true },
                      { label: '否', value: false },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="入库时间" name="ingested_at_range" style={{ marginBottom: 0 }}>
                  <RangePicker
                    style={{ width: '100%' }}
                    showTime={{ format: 'HH:mm:ss' }}
                    format="YYYY-MM-DD HH:mm:ss"
                    placeholder={['开始时间', '结束时间']}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="标题" name="title" style={{ marginBottom: 0 }}>
                  <Input placeholder="请输入标题" style={{ width: '100%' }} />
                </Form.Item>
              </Col>



              <Col span={6}>
                <Form.Item label="创作者ID(openid)" name="creator_user_id" style={{ marginBottom: 0 }}>
                  <Input placeholder="请输入创作者openid" style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="风险状态" name="risk_flag" style={{ marginBottom: 0 }}>
                  <Select
                    style={{ width: '100%' }}
                    allowClear
                    options={[
                      { label: '是', value: true },
                      { label: '否', value: false },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="内容状态" name="status" style={{ marginBottom: 0 }}>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    options={[
                      { label: '待发布', value: '待发布' },
                      { label: '已发布', value: '已发布' },
                      { label: '作废', value: '作废' },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="内容ID" name="content_id" style={{ marginBottom: 0 }}>
                  <Input placeholder="请输入内容ID" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="关键词" name="keyword" style={{ marginBottom: 0 }}>
                  <Input placeholder="请输入关键词" style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="投递平台" name="available_platforms" style={{ marginBottom: 0 }}>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    options={[
                      { label: '微信公众号', value: '微信公众号' },
                      { label: '微信视频号', value: '微信视频号' },
                      { label: '小红书', value: '小红书' },
                      { label: '抖音', value: '抖音' },
                      { label: '快手', value: '快手' },
                      { label: '百家号', value: '百家号' },
                      { label: '知乎', value: '知乎' },
                      { label: 'B站', value: 'B站' },
                      { label: '今日头条', value: '今日头条' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="内容类型" name="content_type" style={{ marginBottom: 0 }}>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    options={[
                      { label: '视频', value: '视频' },
                      { label: '图文', value: '图文' },
                      { label: '笔记', value: '笔记' },
                    ]}
                  />
                </Form.Item>
              </Col>

            </Row>
          </Col>

          <Col span={6} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Space direction="vertical" size="middle" style={{ width: '100%', alignItems: 'center' }}>
              <Button
                // type="primary"
                icon={<CloudUploadOutlined />}
                style={{ width: 200 }}
                onClick={() => setUploadModalVisible(true)}
              >
                上传内容
              </Button>
              <Button
                icon={<EditOutlined />}
                style={{ width: 200 }}
                onClick={() => setXhsNoteModalVisible(true)}
              >
                新建小红书笔记
              </Button>
              <Button type="primary" style={{ width: 200 }} onClick={handleSearch}>
                检索
              </Button>
            </Space>
          </Col>
        </Row>
        </Form>
      </div>

      <div className="page-panel__body">
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="content_id"
          loading={loading}
          scroll={{ x: 1200 }}
          rowSelection={{
            selectedRowKeys: selectedRowKeys.filter(key =>
              // 显示当前页的选中项，以及之前在其他页选中的项
              dataSource.some(item => item.content_id === key) ||
              !dataSource.some(item => item.content_id === key)
            ),
            onChange: (keys, rows) => {
              // 获取当前页的所有 content_id
              const currentPageIds = dataSource.map(item => item.content_id);

              // 获取之前在其他页选中的项（不在当前页的）
              const otherPageSelected = selectedRowKeys.filter(
                key => !currentPageIds.includes(key)
              );

              // 合并：其他页已选中的项 + 当前页新选中的项
              const allSelectedKeys = [...otherPageSelected, ...keys];

              // 去重
              const uniqueKeys = Array.from(new Set(allSelectedKeys));

              setSelectedRowKeys(uniqueKeys);

              // 更新类型缓存：保留其他页已知类型 + 用当前页 rows 补全
              setSelectedTypeById((prev) => {
                const next = {};
                for (const k of uniqueKeys) {
                  if (prev && prev[k]) next[k] = prev[k];
                }
                for (const row of rows || []) {
                  const id = row?.content_id;
                  if (!id) continue;
                  const t = normalizeContentType(row?.content_type);
                  if (t) next[id] = t;
                }
                return next;
              });
            },
          }}
          pagination={false}
        />
      </div>

      <div className="page-panel__bottom" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px 0',
        background: '#fff',
      }}>
        {/* 左侧：批量操作按钮 */}
        <div>
          {selectedRowKeys.length > 0 && (
            <Space>
              <Button onClick={toggleSelectAllCurrentPage}>
                {isAllCurrentPageSelected ? '取消全选(本页)' : '全选本页'}
              </Button>
              <span>已选择 {selectedRowKeys.length} 项</span>
              {!selectedRowKeys.some((id) => {
                const t = normalizeContentType(selectedTypeById?.[id]);
                return t === '图文' || t === '笔记';
              }) && (
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleBatchDownload}
                >
                  批量下载
                </Button>
              )}
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleBatchDelete}
              >
                批量删除
              </Button>
              <Button
                onClick={() => setSelectedRowKeys([])}
              >
                清空全部
              </Button>
            </Space>
          )}
        </div>

        {/* 右侧：分页器 */}
        <div>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showTotal={(total) => `共 ${total} 条记录`}
            showSizeChanger
            showQuickJumper
            style={{ margin: 0 }}
            onChange={(page, pageSize) => {
              if (listQueryModeRef.current === 'search') {
                loadSearchData(page, pageSize);
                return;
              }
              loadData({ page, page_size: pageSize });
              // 保留跨页选择，不清空选中项
            }}
            onShowSizeChange={(current, size) => {
              if (listQueryModeRef.current === 'search') {
                loadSearchData(1, size);
                return;
              }
              loadData({ page: 1, page_size: size });
              // 保留跨页选择，不清空选中项
            }}
          />
        </div>
      </div>

      {/* 内容详情抽屉 */}
      <Drawer
        title="内容详情"
        placement="right"
        width={600}
        onClose={() => {
          setDrawerVisible(false);
          setContentDetail(null);
          detailForm.resetFields();
        }}
        open={drawerVisible}
        loading={detailLoading}
        mask={true}
        maskStyle={{ backdropFilter: 'none', WebkitBackdropFilter: 'none' }}
      >
        {contentDetail && (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'preview',
                label: '内容预览',
                children: (
                  <div style={{ padding: '16px 0' }}>
                    <div style={{ marginBottom: 24 }}>
                      <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>标题</h3>
                      <p style={{ fontSize: 16, color: '#333' }}>{contentDetail.title}</p>
                    </div>
                    <div style={{ marginBottom: 24 }}>
                      <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>内容简介</h3>
                      <p style={{ fontSize: 14, color: '#666', lineHeight: 1.8 }}>{contentDetail.summary || '-'}</p>
                    </div>
                    {contentDetail.media_urls && contentDetail.media_urls.length > 0 && (() => {
                      const mediaUrls = Array.isArray(contentDetail.media_urls) ? contentDetail.media_urls : [];
                      const treatAllAsVideo = isVideoType(contentDetail.content_type);
                      const videoUrls = treatAllAsVideo ? mediaUrls : mediaUrls.filter((u) => isVideoUrl(u));
                      const imageUrls = treatAllAsVideo ? [] : mediaUrls.filter((u) => isImageUrl(u));
                      const otherUrls = treatAllAsVideo
                        ? []
                        : mediaUrls.filter((u) => !isVideoUrl(u) && !isImageUrl(u));

                      // Heuristic: for non-video content, treat unknown URLs as images (common for signed OSS URLs without extension)
                      const imageUrlsFinal = !treatAllAsVideo && imageUrls.length === 0 && otherUrls.length > 0
                        ? otherUrls
                        : imageUrls;
                      const otherUrlsFinal = !treatAllAsVideo && imageUrls.length === 0 ? [] : otherUrls;

                      if (videoUrls.length === 0 && imageUrlsFinal.length === 0 && otherUrlsFinal.length === 0) return null;

                      return (
                        <div style={{ marginBottom: 24 }}>
                          <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>
                            {videoUrls.length > 0 && imageUrlsFinal.length > 0 ? '媒体文件' : (videoUrls.length > 0 ? '视频预览' : '图片预览')}
                          </h3>

                          {imageUrlsFinal.length > 0 && (
                            <Image.PreviewGroup>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                                {imageUrlsFinal.map((url, index) => (
                                  <Image
                                    key={`img_${index}`}
                                    src={url}
                                    alt={`图片 ${index + 1}`}
                                    style={{
                                      width: 160,
                                      height: 160,
                                      objectFit: 'cover',
                                      borderRadius: 8,
                                      border: '1px solid #f0f0f0',
                                    }}
                                    preview={{ mask: '预览' }}
                                  />
                                ))}
                              </div>
                            </Image.PreviewGroup>
                          )}

                          {videoUrls.length > 0 && (
                            <div style={{ marginTop: imageUrlsFinal.length > 0 ? 16 : 0 }}>
                              {videoUrls.map((url, index) => (
                                <video
                                  key={`vid_${index}`}
                                  src={url}
                                  controls
                                  style={{ width: '100%', maxWidth: 600, marginBottom: 16, borderRadius: 8, backgroundColor: '#000' }}
                                />
                              ))}
                            </div>
                          )}

                          {otherUrlsFinal.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                              {otherUrlsFinal.map((url, index) => (
                                <div
                                  key={`other_${index}`}
                                  style={{
                                    padding: '10px 12px',
                                    background: '#f5f5f5',
                                    borderRadius: 8,
                                    wordBreak: 'break-all',
                                    marginBottom: 8,
                                  }}
                                >
                                  <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
                                    {url}
                                  </a>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {(contentDetail.body || contentDetail.content) && (
                      <div style={{ marginBottom: 24 }}>
                        <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>文字内容</h3>
                        {(() => {
                          const body = contentDetail.body ?? contentDetail.content ?? '';
                          if (!body) return null;
                          const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(body);
                          if (looksLikeHtml) {
                            return (
                              <div
                                style={{
                                  border: '1px solid #f0f0f0',
                                  borderRadius: 8,
                                  background: '#fff',
                                  overflow: 'hidden',
                                  maxHeight: 420,
                                }}
                              >
                                <iframe
                                  title="content-html-preview"
                                  // 用 iframe 隔离内容中的 <style>/<script>，避免影响宿主页面布局
                                  srcDoc={String(body)}
                                  sandbox=""
                                  style={{
                                    display: 'block',
                                    width: '100%',
                                    height: 420,
                                    border: 0,
                                  }}
                                />
                              </div>
                            );
                          }
                          return (
                            <p style={{ fontSize: 14, color: '#666', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                              {body}
                            </p>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: 'edit',
                label: '内容编辑',
                children: (
                  <Form
                    form={detailForm}
                    layout="horizontal"
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 16 }}
                    style={{ padding: '16px 0' }}
                  >
                    {contentDetail.cover_image && (
                      <Form.Item label="封面图">
                        <Image
                          src={contentDetail.cover_image}
                          alt="封面图"
                          style={{ maxWidth: 300, maxHeight: 200 }}
                        />
                      </Form.Item>
                    )}
                    <Form.Item
                      label="标题"
                      name="title"
                      rules={[{ required: true, message: '请输入标题' }]}
                    >
                      <Input placeholder="请输入标题" />
                    </Form.Item>
                    <Form.Item label="发布时间">
                      <span>{formatDateTime(contentDetail.created_at)}</span>
                    </Form.Item>
                    <Form.Item
                      label="内容简介"
                      name="summary"
                    >
                      <Input.TextArea rows={4} placeholder="请输入内容简介" />
                    </Form.Item>
                    <Form.Item
                      label="内容"
                      name="body"
                    >
                      <Input.TextArea rows={6} placeholder="请输入内容" />
                    </Form.Item>
                    <Form.Item label="内容长度">
                      <span>{contentDetail.word_count || 0} 字</span>
                    </Form.Item>
                    <Form.Item label="创作者">
                      <span>{fixMojibakeUtf8(contentDetail.author) || '-'}</span>
                    </Form.Item>
                    <Form.Item
                      label="创作者ID(openid)"
                      name="creator_user_id"
                    >
                      <Input placeholder="请输入创作者openid" />
                    </Form.Item>
                    {contentDetail.media_urls && contentDetail.media_urls.length > 0 && (
                      <Form.Item label="内容链接">
                        <div>
                          {contentDetail.media_urls.map((url, index) => (
                            <a key={index} href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginBottom: 8 }}>
                              {url}
                            </a>
                          ))}
                        </div>
                      </Form.Item>
                    )}
                    <Form.Item label="内容大小">
                      <span>{contentDetail.size_bytes ? `${(contentDetail.size_bytes / 1024 / 1024).toFixed(2)} MB` : '-'}</span>
                    </Form.Item>
                    {contentDetail.duration_seconds && (
                      <Form.Item label="视频时长">
                        <span>{contentDetail.duration_seconds} 秒</span>
                      </Form.Item>
                    )}
                    <Form.Item
                      label="标签"
                      name="tags"
                    >
                      <Select
                        mode="tags"
                        placeholder="回车添加标签"
                        tokenSeparators={[',', '，']}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                    <Form.Item
                      label="内容来源"
                      name="source"
                    >
                      <Select
                        style={{ width: '100%' }}
                        allowClear
                        options={[
                          { label: '自建', value: '自建' },
                          { label: '抓取', value: '抓取' },
                          { label: 'AI生成', value: 'AI生成' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item
                      label="内容类型"
                      name="content_type"
                    >
                      <Select
                        style={{ width: '100%' }}
                        allowClear
                        options={[
                          { label: '视频', value: '视频' },
                          { label: '图文', value: '图文' },
                          { label: '笔记', value: '笔记' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item
                      label="一级分类"
                      name="primary_category"
                    >
                      <Input placeholder="请输入一级分类" />
                    </Form.Item>
                    <Form.Item
                      label="二级分类"
                      name="secondary_category"
                    >
                      <Input placeholder="请输入二级分类" />
                    </Form.Item>
                    <Form.Item
                      label="风险状态"
                      name="risk_flag"
                    >
                      <Select
                        style={{ width: '100%' }}
                        allowClear
                        options={[
                          { label: '是', value: true },
                          { label: '否', value: false },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item
                      label="是否需要人工审核"
                      name="manual_review_required"
                    >
                      <Select
                        style={{ width: '100%' }}
                        allowClear
                        options={[
                          { label: '是', value: true },
                          { label: '否', value: false },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item
                      label="是否审核"
                      name="is_manual_reviewed"
                    >
                      <Select
                        style={{ width: '100%' }}
                        allowClear
                        options={[
                          { label: '是', value: true },
                          { label: '否', value: false },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item
                      label="内容状态"
                      name="status"
                    >
                      <Select
                        style={{ width: '100%' }}
                        allowClear
                        options={[
                          { label: '待发布', value: '待发布' },
                          { label: '已发布', value: '已发布' },
                          { label: '作废', value: '作废' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item
                      label="投递平台"
                      name="available_platforms"
                    >
                      <Select
                        mode="multiple"
                        allowClear
                        style={{ width: '100%' }}
                        options={[
                          { label: '微信公众号', value: '微信公众号' },
                          { label: '微信视频号', value: '微信视频号' },
                          { label: '小红书', value: '小红书' },
                          { label: '抖音', value: '抖音' },
                          { label: '快手', value: '快手' },
                          { label: '百家号', value: '百家号' },
                          { label: '知乎', value: '知乎' },
                          { label: 'B站', value: 'B站' },
                          { label: '今日头条', value: '今日头条' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item>
                      <Space>
                        <Button
                          type="primary"
                          onClick={async () => {
                            try {
                              const values = await detailForm.validateFields();
                              // 处理标签：如果是字符串，转换为数组
                              const submitValues = {
                                ...values,
                                tags: values.tags ? (Array.isArray(values.tags) ? values.tags.map((t) => String(t || '').trim()).filter(Boolean) : String(values.tags).split(/[,，]/).map((t) => t.trim()).filter(Boolean)) : [],
                              };
                              const response = await modifyContentDetail({
                                content_id: contentDetail.content_id,
                                ...submitValues,
                              });
                              if (response.status === 'success') {
                                message.success('保存成功');
                                const contentId = contentDetail.content_id;
                                setDataSource((prev) => (prev || []).map((item) => {
                                  if (item?.content_id !== contentId) return item;
                                  return {
                                    ...item,
                                    ...submitValues,
                                    risk_flag: submitValues.risk_flag,
                                    primary_category: submitValues.primary_category,
                                    secondary_category: submitValues.secondary_category,
                                    category_level1: submitValues.primary_category,
                                    category_level2: submitValues.secondary_category,
                                  };
                                }));
                                // 关闭抽屉
                                setDrawerVisible(false);
                                setContentDetail(null);
                                detailForm.resetFields();
                                // 刷新列表
                                await reloadListAfterUpdate();
                              } else {
                                message.error('保存失败');
                              }
                            } catch (error) {
                              console.error('保存失败:', error);
                              message.error('保存失败，请稍后重试');
                            }
                          }}
                        >
                          保存
                        </Button>
                        <Button onClick={() => {
                          detailForm.setFieldsValue({
                            title: contentDetail.title,
                            summary: contentDetail.summary,
                            body: contentDetail.body ?? contentDetail.content ?? '',
                            tags: _normalizeTagsArray(contentDetail.tags),
                            source: contentDetail.source,
                            content_type: contentDetail.content_type,
                            creator_user_id: contentDetail.creator_user_id,
                            risk_flag: contentDetail.risk_flag,
                            status: contentDetail.status,
                            available_platforms: contentDetail.available_platforms || [],
                            manual_review_required: contentDetail.manual_review_required,
                            is_manual_reviewed: contentDetail.is_manual_reviewed,
                            primary_category: contentDetail.primary_category,
                            secondary_category: contentDetail.secondary_category,
                          });
                        }}>
                          重置
                        </Button>
                      </Space>
                    </Form.Item>
                  </Form>
                ),
              },
            ]}
          />
        )}
      </Drawer>

      <Modal
        title="新建小红书笔记"
        open={xhsNoteModalVisible}
        onOk={handleCreateXhsNote}
        confirmLoading={xhsNoteCreating}
        okText="创建"
        cancelText="取消"
        width={900}
        onCancel={() => {
          setXhsNoteModalVisible(false);
          resetXhsNoteModal();
        }}
      >
        <Form
          form={xhsNoteForm}
          layout="vertical"
          initialValues={{ tags: [] }}
        >
          <Form.Item
            label="标题"
            name="title"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input placeholder="请输入标题" maxLength={20} showCount />
          </Form.Item>

          <Form.Item
            label={(
              <Space size="small">
                <span>内容简介</span>
                <Button
                  type="link"
                  size="small"
                  loading={xhsNoteSummaryGenerating}
                  onClick={(e) => {
                    e.preventDefault();
                    handleGenerateXhsNoteSummary();
                  }}
                >
                  AI生成
                </Button>
              </Space>
            )}
            name="summary"
            extra="可手动填写；也可点击 AI生成（基于正文）。"
          >
            <Input.TextArea rows={3} placeholder="用于列表展示的简介（可选）" />
          </Form.Item>

          <Form.Item
            label="正文"
            name="body"
            rules={[{ required: true, message: '请输入正文' }]}
          >
            <Input.TextArea rows={8} placeholder="请输入正文" />
          </Form.Item>

          <Form.Item
            label={(
              <Space size="small">
                <span>标签</span>
                <Button
                  type="link"
                  size="small"
                  loading={xhsNoteTagsGenerating}
                  onClick={(e) => {
                    e.preventDefault();
                    handleGenerateXhsNoteTags();
                  }}
                >
                  AI生成
                </Button>
              </Space>
            )}
            name="tags"
          >
            <Select mode="tags" placeholder="回车添加标签" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="图片（拖动可调整顺序）"
            required
            extra="最多 18 张，支持拖拽排序和删除。"
          >
            <Upload
              multiple
              accept=".jpg,.jpeg,.png,.gif,.webp,.bmp"
              beforeUpload={() => false}
              fileList={xhsNoteImageFileList}
              onChange={handleXhsNoteImagesChange}
              showUploadList={false}
              disabled={xhsNoteCreating}
            >
              <Button icon={<CloudUploadOutlined />} disabled={xhsNoteCreating}>选择图片</Button>
            </Upload>

	            <div
	              style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}
	              onDragOver={(e) => {
	                e.preventDefault();
	                if (e.target === e.currentTarget && xhsNoteDragUid) {
	                  if (xhsNoteDropHint?.uid !== '__end__' || xhsNoteDropHint?.position !== 'end') {
	                    setXhsNoteDropHint({ uid: '__end__', position: 'end' });
	                  }
	                }
	              }}
	              onDrop={(e) => {
	                if (e.target !== e.currentTarget) return;
	                e.preventDefault();
	                const fromUid = _readXhsNoteDragUid(e);
	                handleXhsNoteDropToEnd(fromUid);
	              }}
	            >
	              {(xhsNoteImageFileList || []).map((f, idx) => {
	                const hint = xhsNoteDropHint?.uid === f.uid ? xhsNoteDropHint.position : null;
	                const borderStyle = hint === 'before'
	                  ? { borderLeft: '3px solid #1677ff' }
	                  : hint === 'after'
	                    ? { borderRight: '3px solid #1677ff' }
	                    : {};

	                return (
	                  <div
	                    key={f.uid}
	                    draggable
	                    onDragStart={(e) => {
	                      try {
	                        e.dataTransfer.effectAllowed = 'move';
	                        e.dataTransfer.setData('application/xhsnote-image-uid', f.uid);
	                        e.dataTransfer.setData('text/plain', f.uid);
	                      } catch (_) {
	                        // ignore
	                      }
	                      setXhsNoteDragUid(f.uid);
	                    }}
	                    onDragEnd={() => {
	                      setXhsNoteDragUid(null);
	                      setXhsNoteDropHint(null);
	                    }}
	                    onDragOver={(e) => {
	                      e.preventDefault();
	                      try {
	                        const rect = e.currentTarget.getBoundingClientRect();
	                        const x = e.clientX - rect.left;
	                        const pos = x < rect.width / 2 ? 'before' : 'after';
	                        if (xhsNoteDropHint?.uid !== f.uid || xhsNoteDropHint?.position !== pos) {
	                          setXhsNoteDropHint({ uid: f.uid, position: pos });
	                        }
	                      } catch (_) {
	                        // ignore
	                      }
	                    }}
	                    onDrop={(e) => {
	                      e.preventDefault();
	                      e.stopPropagation();
	                      const fromUid = _readXhsNoteDragUid(e);
	                      let pos = 'before';
	                      try {
	                        const rect = e.currentTarget.getBoundingClientRect();
	                        const x = e.clientX - rect.left;
	                        pos = x < rect.width / 2 ? 'before' : 'after';
	                      } catch (_) {
	                        pos = xhsNoteDropHint?.uid === f.uid ? xhsNoteDropHint.position : 'before';
	                      }
	                      handleXhsNoteDropOn(f.uid, pos, fromUid);
	                    }}
	                    style={{
	                      width: 120,
	                      border: xhsNoteDragUid === f.uid ? '1px dashed #1677ff' : '1px solid #f0f0f0',
	                      borderRadius: 8,
	                      padding: 6,
	                      background: '#fff',
	                      cursor: 'move',
	                      ...borderStyle,
	                    }}
	                  >
	                    <div style={{ position: 'relative' }}>
	                      <img
	                        src={_getXhsNotePreviewUrl(f)}
	                        alt={f.name}
	                        style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 6 }}
	                      />
	                      <Button
	                        danger
	                        size="small"
	                        type="primary"
	                        icon={<DeleteOutlined />}
	                        onClick={(e) => {
	                          e.preventDefault();
	                          e.stopPropagation();
	                          handleXhsNoteRemoveImage(f.uid);
	                        }}
	                        style={{ position: 'absolute', top: 6, right: 6 }}
	                        disabled={xhsNoteCreating}
	                      />
	                    </div>
	                    <div style={{ fontSize: 12, marginTop: 6, color: '#666' }}>
	                      {idx + 1}/{(xhsNoteImageFileList || []).length}
	                    </div>
	                  </div>
	                );
	              })}
	              <div
	                key="__xhs_note_end_drop__"
	                onDragOver={(e) => {
	                  e.preventDefault();
	                  if (xhsNoteDropHint?.uid !== '__end__' || xhsNoteDropHint?.position !== 'end') {
	                    setXhsNoteDropHint({ uid: '__end__', position: 'end' });
	                  }
	                }}
	                onDrop={(e) => {
	                  e.preventDefault();
	                  const fromUid = _readXhsNoteDragUid(e);
	                  handleXhsNoteDropToEnd(fromUid);
	                }}
	                style={{
	                  flexBasis: '100%',
	                  height: 44,
	                  border: xhsNoteDropHint?.uid === '__end__' ? '2px dashed #1677ff' : '1px dashed #d9d9d9',
	                  borderRadius: 8,
	                  background: '#fafafa',
	                  color: '#666',
	                  fontSize: 12,
	                  display: (xhsNoteImageFileList || []).length > 0 ? (xhsNoteDragUid ? 'flex' : 'none') : 'none',
	                  alignItems: 'center',
	                  justifyContent: 'center',
	                  userSelect: 'none',
	                }}
	              >
	                拖到这里放到最后
	              </div>
	            </div>
	          </Form.Item>
	        </Form>
	      </Modal>

      {/* 上传内容对话框 */}
      <Modal
        title="上传内容"
        open={uploadModalVisible}
        onOk={handleUpload}
        onCancel={() => {
          setUploadModalVisible(false);
          uploadForm.resetFields();
          setFileList([]);
          setUploadItems([]);
          setSelectedUploadUid(null);
          uploadItemForm.resetFields();
        }}
        confirmLoading={uploading}
        okText="上传"
        cancelText="取消"
        width={900}
      >
        <Form
          form={uploadForm}
          layout="vertical"
          initialValues={{
            source: '自建',
            status: '待发布',
          }}
        >
          <Form.Item
            label="创作者"
          >
            <Input value={user?.nickname || user?.name || user?.user_id || '-'} disabled />
          </Form.Item>

          <Form.Item
            label="选择文件"
            required
            rules={[
              {
                validator: () => {
                  if (fileList.length === 0) {
                    return Promise.reject(new Error('请至少选择一个文件'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Upload
              multiple
              fileList={fileList}
              onChange={handleFileChange}
              onRemove={handleRemove}
              beforeUpload={() => false} // 阻止自动上传
              accept=".mp4,.avi,.mov,.wmv,.flv,.mkv,.doc,.docx,.pdf,.jpg,.jpeg,.png,.gif"
            >
              <Button icon={<CloudUploadOutlined />}>选择文件</Button>
              <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                支持视频（.mp4等）、图文（Word文件）、笔记（图片+文档）
              </div>
            </Upload>
          </Form.Item>

          {uploadItems.length > 0 && (
            <div style={{ display: 'flex', gap: 12, border: '1px solid #f0f0f0', padding: 12, borderRadius: 8, marginBottom: 12 }}>
              <div style={{ width: 220 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>文件列表</div>
                <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                  {(uploadItems || []).map((it) => {
                    const active = it.uid === selectedUploadUid;
                    const statusText = it.aiStatus === 'loading' ? '生成中' : it.aiStatus === 'done' ? '已生成' : it.aiStatus === 'error' ? '生成失败' : '未生成';
                    const statusColor = it.aiStatus === 'done' ? '#52c41a' : it.aiStatus === 'error' ? '#ff4d4f' : it.aiStatus === 'loading' ? '#1677ff' : '#999';
                    return (
                      <div
                        key={it.uid}
                        onClick={() => selectUploadItem(it.uid)}
                        style={{
                          cursor: 'pointer',
                          padding: '8px 10px',
                          borderRadius: 6,
                          border: active ? '1px solid #1677ff' : '1px solid #f0f0f0',
                          background: active ? '#e6f4ff' : '#fff',
                          marginBottom: 8,
                        }}
                      >
                        <div style={{ fontSize: 12, fontWeight: 600, wordBreak: 'break-all' }}>{it.name}</div>
                        <div style={{ fontSize: 12, color: statusColor }}>{statusText}</div>
                        {it.aiStatus === 'error' && it.aiError ? (
                          <div style={{ fontSize: 12, color: '#ff4d4f', wordBreak: 'break-all' }}>{it.aiError}</div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>当前文件元数据</div>
                {!selectedUploadItem ? (
                  <div style={{ color: '#999' }}>请选择一个文件</div>
                ) : (
                    <Form
                      form={uploadItemForm}
                      layout="vertical"
                      onValuesChange={(changed) => {
                        if (suppressUploadItemSyncRef.current) return;
                        // mark per-file source config as customized when user changes it
                        if (changed && (Object.prototype.hasOwnProperty.call(changed, 'text_source') || Object.prototype.hasOwnProperty.call(changed, 'ocr_max_frames'))) {
                          setUploadItems((prev) =>
                            (prev || []).map((it) =>
                              it.uid === selectedUploadUid ? { ...it, text_source_customized: true } : it
                            )
                          );
                        }
                        syncSelectedItemFromForm();
                      }}
                    >
                    <Form.Item label="AI文本来源" name="text_source" initialValue="asr">
                      <Radio.Group
                        disabled={batchGenerating}
                        options={[
                          { label: 'ASR（语音）', value: 'asr' },
                          { label: 'OCR（抽帧）', value: 'image' },
                          { label: '自动', value: 'auto' },
                        ]}
                        optionType="button"
                        buttonStyle="solid"
                      />
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevVals, curVals) => prevVals?.text_source !== curVals?.text_source}
                    >
                      {({ getFieldValue }) => {
                        const ts = String(getFieldValue('text_source') || 'asr');
                        if (ts === 'asr') return null;
                        return (
                          <Form.Item
                            label="OCR抽帧数"
                            name="ocr_max_frames"
                            extra="仅在 OCR/自动 模式下生效；帧数越多越慢。"
                          >
                            <InputNumber min={1} max={200} style={{ width: 220 }} disabled={batchGenerating} />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <Form.Item label="标题" name="title">
                      <Input
                        disabled={batchGenerating}
                        placeholder="标题（默认文件名）"
                        addonAfter={
                          <Button
                            type="link"
                            size="small"
		                            loading={previewing && previewingAction === 'title'}
		                            disabled={batchGenerating || (previewing && previewingAction !== 'title')}
		                            onClick={(e) => {
		                              e.preventDefault();
		                              doPreviewForSelected('title');
		                            }}
		                          >
                            AI生成
                          </Button>
                        }
                      />
                    </Form.Item>
                    <Form.Item
                      label="内容简介"
                      name="summary"
                      extra="建议先点击 AI生成；如不合适可直接修改。"
                    >
                      <Input.TextArea
                        disabled={batchGenerating}
                        rows={4}
                        placeholder="点击右侧 AI生成 生成内容简介"
                      />
                    </Form.Item>
                    <Form.Item label="标签" name="tags">
                      <Select
                        mode="tags"
                        disabled={batchGenerating}
                        placeholder="回车添加标签"
                        style={{ width: '100%' }}
                        onBlur={() => syncSelectedItemFromForm()}
                      />
                    </Form.Item>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
		                      <Button
		                        type="primary"
		                        loading={previewing && previewingAction === 'all'}
		                        disabled={batchGenerating || (previewing && previewingAction !== 'all')}
		                        onClick={() => doPreviewForSelected('all')}
		                      >
		                        AI生成简介/标签
		                      </Button>
	                    </div>
	                  </Form>
                )}
              </div>
            </div>
	          )}

            {uploadItems.length >= 2 && (
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>批量辅助</div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 360 }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>全局 AI 文本来源（仅应用到未修改的文件）</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Radio.Group
                        value={globalTextSource}
                        onChange={(e) => setGlobalTextSource(e?.target?.value)}
                        optionType="button"
                        buttonStyle="solid"
                        disabled={batchGenerating || previewing}
                        options={[
                          { label: 'ASR（语音）', value: 'asr' },
                          { label: 'OCR（抽帧）', value: 'image' },
                          { label: '自动', value: 'auto' },
                        ]}
                      />
                      {(globalTextSource === 'image' || globalTextSource === 'auto') && (
                        <InputNumber
                          min={1}
                          max={200}
                          value={globalOcrMaxFrames}
                          onChange={(v) => setGlobalOcrMaxFrames(Number(v || 6))}
                          style={{ width: 140 }}
                          disabled={batchGenerating || previewing}
                        />
                      )}
                      <Button onClick={applyGlobalTextSourceToUntouched} disabled={batchGenerating || previewing}>
                        应用到未修改文件
                      </Button>
                    </div>
                  </div>

                  <div style={{ flex: 1, minWidth: 360 }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>一键为所有内容生成 标题/简介/标签（并发=3）</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Select
                        value={batchSkipDone ? 'skip' : 'all'}
                        onChange={(v) => setBatchSkipDone(v === 'skip')}
                        style={{ width: 180 }}
                        disabled={batchGenerating || previewing}
                        options={[
                          { label: '跳过已生成', value: 'skip' },
                          { label: '包含已生成', value: 'all' },
                        ]}
                      />
                      <Button
                        type="primary"
                        loading={batchGenerating}
                        disabled={previewing}
                        onClick={() => runBatchGenerate({ retryOnlyFailures: false })}
                      >
                        一键生成全部
                      </Button>
                      <Button
                        disabled={batchGenerating || previewing || (batchFailures || []).length === 0}
                        onClick={() => runBatchGenerate({ retryOnlyFailures: true })}
                      >
                        失败重试
                      </Button>
                    </div>

                    {batchProgress.total > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <Progress
                          percent={Math.round((batchProgress.done / Math.max(batchProgress.total, 1)) * 100)}
                          status={batchGenerating ? 'active' : (batchProgress.failed > 0 ? 'exception' : 'success')}
                        />
                        <div style={{ fontSize: 12, color: '#666' }}>
                          进度：{batchProgress.done}/{batchProgress.total}；成功：{batchProgress.ok}；失败：{batchProgress.failed}
                        </div>
                      </div>
                    )}

                    {(batchFailures || []).length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>失败列表</div>
                        <Table
                          size="small"
                          pagination={false}
                          dataSource={(batchFailures || []).map((it, idx) => ({ key: it.uid || String(idx), ...it }))}
                          columns={[
                            { title: '文件', dataIndex: 'name', key: 'name', width: 220, render: (v) => <div style={{ wordBreak: 'break-all' }}>{v}</div> },
                            { title: '原因', dataIndex: 'error', key: 'error', render: (v) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{String(v || '')}</div> },
                          ]}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

	          <Form.Item
	            label="内容来源"
	            name="source"
	          >
            <Select
              style={{ width: '100%' }}
              options={[
                { label: '自建', value: '自建' },
                { label: '抓取', value: '抓取' },
                { label: 'AI生成', value: 'AI生成' },
              ]}
            />
          </Form.Item>

          <Form.Item
            label="投递平台"
            name="available_platforms"
          >
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="请选择投递平台（可选）"
              options={[
                { label: '微信公众号', value: '微信公众号' },
                { label: '微信视频号', value: '微信视频号' },
                { label: '小红书', value: '小红书' },
                { label: '抖音', value: '抖音' },
                { label: '快手', value: '快手' },
                { label: '百家号', value: '百家号' },
                { label: '知乎', value: '知乎' },
                { label: 'B站', value: 'B站' },
                { label: '今日头条', value: '今日头条' },
              ]}
            />
          </Form.Item>

          <Form.Item
            label="内容状态"
            name="status"
          >
            <Select
              style={{ width: '100%' }}
              options={[
                { label: '待发布', value: '待发布' },
                { label: '已发布', value: '已发布' },
                { label: '草稿', value: '草稿' },
                { label: '已下线', value: '已下线' },
                { label: '作废', value: '作废' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 发布账号选择抽屉 */}
      <Drawer
        title="选择发布账号"
        placement="right"
        width={800}
        onClose={() => {
          setPublishAccountDrawerVisible(false);
          setCurrentPublishRecord(null);
          setAccountDataSource([]);
          setSelectedAccountKeys([]);
          setSelectedAccounts([]);
          selectedAccountMapRef.current.clear();
          accountFilterForm.resetFields();
          setAccountPagination({
            current: 1,
            pageSize: 10,
            total: 0,
          });
        }}
        open={publishAccountDrawerVisible}
        loading={accountLoading}
        mask={true}
        maskStyle={{ backdropFilter: 'none', WebkitBackdropFilter: 'none' }}
        footer={
          <div style={{ textAlign: 'right', padding: '10px 0' }}>
            <Space>
              <Button onClick={() => {
                setPublishAccountDrawerVisible(false);
                setSelectedAccountKeys([]);
                setSelectedAccounts([]);
                selectedAccountMapRef.current.clear();
              }}>
                取消
              </Button>
              <Button
                type="primary"
                onClick={handleConfirmAccountSelection}
                disabled={selectedAccountKeys.length === 0}
              >
                确认选择 ({selectedAccountKeys.length})
              </Button>
            </Space>
          </div>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* 筛选表单 */}
          <Form
            form={accountFilterForm}
            layout="inline"
            onFinish={handleAccountFilter}
            style={{ marginBottom: 16 }}
          >
            <Form.Item label="平台" name="platforms">
              <Select
                mode="multiple"
                allowClear
                style={{ width: 200 }}
                placeholder="请选择平台"
                options={[
                  { label: '微信公众号', value: '微信公众号' },
                  { label: '微信视频号', value: '微信视频号' },
                  { label: '小红书', value: '小红书' },
                  { label: '抖音', value: '抖音' },
                  { label: '快手', value: '快手' },
                  { label: '百家号', value: '百家号' },
                  { label: '知乎', value: '知乎' },
                  { label: 'B站', value: 'B站' },
                  { label: '今日头条', value: '今日头条' },
                ]}
              />
            </Form.Item>
            <Form.Item label="状态" name="status">
              <Select
                mode="multiple"
                allowClear
                style={{ width: 200 }}
                placeholder="请选择状态"
                options={[
                  { label: '活跃', value: 'active' },
                  { label: '未激活', value: 'inactive' },
                  { label: '已暂停', value: 'suspended' },
                ]}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                筛选
              </Button>
              <Button
                style={{ marginLeft: 8 }}
                onClick={() => {
                  accountFilterForm.resetFields();
                  handleAccountFilter();
                }}
              >
                重置
              </Button>
            </Form.Item>
          </Form>
          <Table
            className="account-select-table"
            size="small"
            columns={[
              {
                title: '平台',
                dataIndex: 'platform',
                key: 'platform',
                width: 90,
                render: (platform) => {
                  const platformMap = {
                    'xhs': '小红书',
                    'douyin': '抖音',
                    'wechat': '微信公众号',
                    'wechat_channels': '微信视频号',
                    'weixin_channels': '微信视频号',
                    'kuaishou': '快手',
                    'baijiahao': '百家号',
                    'zhihu': '知乎',
                    'bilibili': 'B站',
                    'toutiao': '今日头条',
                  };
                  return platformMap[platform] || platform;
                },
              },
              {
                title: '账号管理员',
                dataIndex: 'operator_user_names',
                key: 'operator_user_id',
                width: 140,
                ellipsis: true,
                render: (operator_user_names) => {
                  if (Array.isArray(operator_user_names)) {
                    const text = operator_user_names.join(', ');
                    return <span title={text}>{text}</span>;
                  }
                  return <span title={operator_user_names || '-'}>{operator_user_names || '-'}</span>;
                },
              },
              {
                title: '账号名',
                dataIndex: 'account_name',
                key: 'account_name',
                width: 180,
                ellipsis: true,
                render: (account_name) => <span title={account_name || '-'}>{account_name || '-'}</span>,
              },
              {
                title: '账号id',
                dataIndex: 'account_id',
                key: 'account_id',
                width: 140,
                ellipsis: true,
                render: (account_id) => <span title={account_id || '-'}>{account_id || '-'}</span>,
              },
              {
                title: '权限',
                dataIndex: 'permission',
                key: 'permission',
                width: 120,
                render: (permission) => (permission?.can_operate === false ? '仅可查看' : '可操作'),
              },
            ]}
            dataSource={accountDataSource}
            rowKey="account_id"
            loading={accountLoading}
            rowSelection={{
              selectedRowKeys: selectedAccountKeys,
              preserveSelectedRowKeys: true,
              getCheckboxProps: (record) => ({
                disabled: record?.permission?.can_operate === false,
              }),
              onSelect: (record, selected) => {
                if (!record?.account_id) return;
                if (selected) {
                  selectedAccountMapRef.current.set(record.account_id, record);
                } else {
                  selectedAccountMapRef.current.delete(record.account_id);
                }
              },
              onSelectAll: (selected, selectedRows, changeRows) => {
                const rows = Array.isArray(changeRows) ? changeRows : [];
                if (selected) {
                  rows.forEach((row) => {
                    if (row?.account_id) selectedAccountMapRef.current.set(row.account_id, row);
                  });
                } else {
                  rows.forEach((row) => {
                    if (row?.account_id) selectedAccountMapRef.current.delete(row.account_id);
                  });
                }
              },
              onChange: (selectedRowKeys) => {
                setSelectedAccountKeys((prevKeys) => {
                  const currentPageIds = (accountDataSource || []).map((a) => a.account_id);
                  const keepFromOtherPages = (prevKeys || []).filter((key) => !currentPageIds.includes(key));
                  const merged = Array.from(new Set([...keepFromOtherPages, ...(selectedRowKeys || [])]));
                  return merged;
                });
              },
            }}
            pagination={{
              current: accountPagination.current,
              pageSize: accountPagination.pageSize,
              total: accountPagination.total,
              showTotal: (total) => `共 ${total} 条记录`,
              showSizeChanger: true,
              showQuickJumper: true,
              onChange: (page, pageSize) => {
                setAccountPagination({
                  ...accountPagination,
                  current: page,
                  pageSize: pageSize,
                });
                loadPublishAccounts({ page, page_size: pageSize });
              },
              onShowSizeChange: (current, size) => {
                setAccountPagination({
                  ...accountPagination,
                  current: 1,
                  pageSize: size,
                });
                loadPublishAccounts({ page: 1, page_size: size });
              },
            }}
            scroll={{ x: 'max-content' }}
          />
        </Space>
      </Drawer>

      {/* 发布时间选择抽屉 */}
      <Drawer
        title="选择发布时间"
        placement="right"
        width={600}
        onClose={() => {
          if (publishSubmitting) {
            abortPublishSubmitRequest();
            message.info({ content: '已取消本次发布请求', key: 'publish_submit' });
          }
          setPublishSubmitting(false);
          setPublishTimeDrawerVisible(false);
          setPublishType('immediate');
          setDispatchMode('default');
          setPreferredAgentId('');
          publishTimeForm.resetFields();
        }}
        open={publishTimeDrawerVisible}
        mask={true}
        maskStyle={{ backdropFilter: 'none', WebkitBackdropFilter: 'none' }}
        footer={
          <div style={{ textAlign: 'right', padding: '10px 0' }}>
            <Space>
              <Button onClick={handleBackToAccountSelection} disabled={publishSubmitting}>
                返回
              </Button>
              <Button
                type="primary"
                onClick={handleConfirmPublish}
                loading={publishSubmitting}
              >
                确认
              </Button>
            </Space>
          </div>
        }
      >
        <Steps
          size="small"
          current={publishSubmitting ? 2 : 1}
          style={{ marginBottom: 16 }}
          items={[
            { title: '选账号', status: 'finish' },
            { title: '选方式', status: publishSubmitting ? 'finish' : 'process' },
            { title: '提交', status: publishSubmitting ? 'process' : 'wait' },
            { title: '查看状态', status: 'wait' },
          ]}
        />

        <Form form={publishTimeForm} layout="vertical">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* 展示选中的账号信息 */}
            <div style={{
              padding: '16px',
              background: '#f5f5f5',
              borderRadius: '4px',
              marginBottom: '24px'
            }}>
              <div style={{ marginBottom: '12px', fontSize: '14px', color: '#333' }}>
                您将发送"<span style={{ fontWeight: 600 }}>{currentPublishRecord?.title || '内容'}</span>"于以下账号：
              </div>
              {selectedAccounts.map((account) => {
                const platformMap = {
                  'xhs': '小红书',
                  'douyin': '抖音',
                  'wechat': '微信公众号',
                  'wechat_channels': '微信视频号',
                  'weixin_channels': '微信视频号',
                  'kuaishou': '快手',
                  'baijiahao': '百家号',
                  'zhihu': '知乎',
                  'bilibili': 'B站',
                  'toutiao': '今日头条',
                };
                return (
                  <div key={account.account_id} style={{
                    marginBottom: '8px',
                    padding: '12px',
                    background: '#fff',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Tag color="blue">
                      {platformMap[account.platform] || account.platform}
                    </Tag>
                    <span style={{ marginRight: '8px' }}>账号ID: {account.account_id}</span>
                    <span>账号名: {account.account_name}</span>
                    <div style={{ marginLeft: 'auto' }}>
                      <Space size="small">
                        <DouyinAccountCheckButton
                          account={account}
                          label="检测账号"
                          buttonProps={{ style: { padding: '0 4px' } }}
                        />
                        <KuaishouAccountCheckButton
                          account={account}
                          label="检测账号"
                          buttonProps={{ style: { padding: '0 4px' } }}
                        />
                        <XhsAccountCheckButton
                          account={account}
                          label="检测账号"
                          restartMcp
                          buttonProps={{ style: { padding: '0 4px' } }}
                        />
                      </Space>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 发布类型选择 */}
            <div>
              <div style={{ marginBottom: '16px', fontWeight: 600, fontSize: '14px' }}>
                选择发布方式：
              </div>
              <Radio.Group
                value={publishType}
                onChange={(e) => {
                  setPublishType(e.target.value);
                  if (e.target.value === 'immediate') {
                    publishTimeForm.setFieldsValue({ task_name: '', scheduled_time: null });
                  }
                }}
                style={{ width: '100%' }}
              >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Radio value="immediate" style={{ fontSize: '14px' }}>
                    立刻发布
                  </Radio>
                  <Radio value="scheduled" style={{ fontSize: '14px' }}>
                    定时发布
                  </Radio>
                </Space>
              </Radio.Group>
            </div>

            {publishType === 'immediate' && (
              <div>
                <div style={{ marginBottom: '16px', fontWeight: 600, fontSize: '14px' }}>
                  执行设备：
                </div>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Radio.Group
                    value={dispatchMode}
                    onChange={(e) => {
                      const nextMode = e.target.value;
                      setDispatchMode(nextMode);
                      if (nextMode !== 'preferred') setPreferredAgentId('');
                    }}
                    style={{ width: '100%' }}
                  >
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Radio value="default">默认设备（账号绑定 Agent）</Radio>
                      <Radio value="preferred">指定 Agent（仅本次）</Radio>
                    </Space>
                  </Radio.Group>
                  {dispatchMode === 'preferred' && (
                    <Select
                      showSearch
                      allowClear
                      value={preferredAgentId || undefined}
                      placeholder="请选择本次执行 Agent"
                      loading={dispatchAgentsLoading}
                      options={(dispatchAgents || []).map((item) => {
                        const agentId = String(item?.agent_id || '').trim();
                        const host = String(item?.hostname || '').trim();
                        const online = item?.is_online === true;
                        const status = online ? '在线' : '离线';
                        return {
                          label: host ? `${host} (${status})` : `${agentId} (${status})`,
                          value: agentId,
                        };
                      }).filter((item) => item.value)}
                      onChange={(value) => setPreferredAgentId(String(value || '').trim())}
                      onOpenChange={(open) => {
                        if (open) loadDispatchAgents({ silent: true });
                      }}
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          <div style={{ padding: 8, borderTop: '1px solid #f0f0f0' }}>
                            <Button size="small" block onClick={() => loadDispatchAgents({ silent: false })}>
                              刷新 Agent 列表
                            </Button>
                          </div>
                        </>
                      )}
                    />
                  )}
                </Space>
              </div>
            )}

            {/* 定时发布相关选项 */}
            {publishType === 'scheduled' && (
              <>
                <Form.Item
                  label="任务名称"
                  name="task_name"
                  rules={[{ required: true, message: '请输入任务名称' }]}
                >
                  <Input
                    placeholder="请输入任务名称"
                  />
                </Form.Item>
                <Form.Item
                  label={selectedAccounts?.length === 1 ? '本次最多发布条数' : '每账号最多发布条数'}
                  name="max_items_per_account_per_run"
                  initialValue={1}
                  rules={[{ required: true, message: '请输入条数' }]}
                >
                  <InputNumber min={1} max={50} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                  label="选择发布时间"
                  name="scheduled_time"
                  rules={[{ required: true, message: '请选择发布时间' }]}
                >
                  <DatePicker
                    showTime
                    format="YYYY-MM-DD HH:mm:ss"
                    style={{ width: '100%' }}
                    placeholder="请选择发布时间"
                  />
                </Form.Item>
              </>
            )}
          </Space>
        </Form>
      </Drawer>
    </div>
  );
};

export default ContentList;
